import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from 'moment-timezone';
import Header from '../components/marketing/header.js';
import consumer from "../channels/consumer";
import { toast } from 'react-toastify';
import useOAuthPopup from '../hooks/useOAuthPopup.js';
import { mirrorConnectionAttrs } from '../utils/connectionAttrs.js';
import { isPostEditable, showsSchedulePicker, requiresFutureTime } from '../utils/companyPostEditability.js';
import { channelsForTargets, targetsForAccountKeys } from '../utils/socialChannelMedia.js';
import ChannelImagePreviews from '../components/marketing/ChannelImagePreviews.js';
import SocialAccountPicker from '../components/marketing/SocialAccountPicker.js';
import useSocialPostAccounts from '../hooks/useSocialPostAccounts.js';
import { persistActivityView } from '../shell/modeResolver.js';
import {
  FACEBOOK_ACCOUNT,
  FACEBOOK_PAGE,
  INSTAGRAM_ACCOUNT,
  REMOVAL_ERROR,
  buildAccountRows,
  buildPageRows,
  cascadeNote,
  clearRemovedKeys,
  confirmBody,
  confirmTitle,
  expandSelection,
  removalToast,
  removeCtaLabel,
  sectionSelectAllLabel,
  selectionLabel,
  toRemovalPayload,
  toggleKey,
  toggleSection,
} from '../utils/connectionSelection.js';

// Helper: format a Date/ISO in a specific IANA timezone as YYYY-MM-DD
const formatDateInTz = (dateLike, tz) => moment.tz(dateLike, tz || 'UTC').format('YYYY-MM-DD');
// Helper: convert an ISO/Date to datetime-local string in event timezone
const toLocalInputForTz = (dateLike, tz) => dateLike ? moment.tz(dateLike, tz || 'UTC').format('YYYY-MM-DDTHH:mm') : '';
// Helper: now in event timezone for input min
const nowLocalInputForTz = (tz) => moment.tz(new Date(), tz || 'UTC').format('YYYY-MM-DDTHH:mm');
// Helper: human-readable time in event timezone (e.g., 8:00 am)
const formatTimeInTz = (dateLike, tz) => dateLike ? moment.tz(dateLike, tz || 'UTC').format('h:mm a') : '';

// Instagram cannot take an image until the server has prepared its framed version; Approve waits
// for that (and refuses if it failed) whenever the post goes to Instagram with an image.
const instagramBlocksApproval = (targets, mediaUrl, mediaType, igStatus) =>
  channelsForTargets(targets).includes('instagram') && !!mediaUrl && !(mediaType || '').includes('video') && igStatus !== 'ready';

const VideoThumbnail = ({ videoUrl, alt, className }) => {
  const [thumbnail, setThumbnail] = useState(null);

  useEffect(() => {
    if (!videoUrl) return;

    const video = document.createElement("video");
    video.src = videoUrl;
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "metadata";

    video.addEventListener("loadedmetadata", () => {
      // Go to the middle of the video
      video.currentTime = video.duration / 2;
    });

    video.addEventListener("seeked", () => {
      // Once it jumps to that time, capture the frame
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      setThumbnail(canvas.toDataURL("image/png"));
    });

    return () => {
      video.removeAttribute("src");
      video.load();
    };
  }, [videoUrl]);

  if (!thumbnail) return null;

  return <img src={thumbnail} alt={alt} className={className} />;
};

const EditPostModal = ({ isOpen, post, onClose, onSave, onDelete, promotedEvents = [] }) => {
  const [editedPost, setEditedPost] = useState(post || {});
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPost, setOriginalPost] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [igStatus, setIgStatus] = useState('idle');
  const uploadcareWidgetRef = React.useRef(null);
  const lastInitializedPostIdRef = React.useRef(null);
  // Anything not yet published (draft, approved, failed, declined) stays fully editable —
  // including posts whose scheduled time has already passed (agent posts land at now+1min).
  const isEditable = isPostEditable(post);

  const inferTargetsFromPlatforms = (platforms) => {
    const list = Array.isArray(platforms) ? platforms : [];
    const hasFb = list.includes('facebook');
    const hasIg = list.includes('instagram');
    if (hasFb && hasIg) return 'both';
    if (hasIg) return 'instagram';
    if (hasFb) return 'facebook';
    return 'both';
  };

  // Dedicated state for the Edit Post "Post on" radios.
  // This avoids scenarios where `editedPost` is replaced while the user clicks.
  const [editSocialMediaPostTargets, setEditSocialMediaPostTargets] = useState(() => {
    return post?.social_media_post_targets || inferTargetsFromPlatforms(post?.platforms);
  });

  // The exact pages/accounts this post goes to. Starts from the post's own pick, or what it will
  // use by default (resolved_target_accounts); only sent back once the user changes it.
  const { accounts: socialAccounts, loading: socialAccountsLoading, error: socialAccountsError } = useSocialPostAccounts(isOpen);
  const [editTargetAccounts, setEditTargetAccounts] = useState([]);
  const handleTargetAccountsChange = (keys) => {
    const targets = targetsForAccountKeys(keys);
    setEditTargetAccounts(keys);
    setEditSocialMediaPostTargets(targets);
    setEditedPost((prev) => ({
      ...prev,
      target_accounts: keys,
      target_accounts_touched: true,
      social_media_post_targets: targets,
      platforms: channelsForTargets(targets)
    }));
    setHasChanges(true);
  };
  const noAccountPicked = socialAccounts.length > 0 && editTargetAccounts.length === 0;

  const includedEventNames = React.useMemo(() => {
    const postEventIds = Array.isArray(editedPost?.event_ids) ? editedPost.event_ids : [];
    if (postEventIds.length === 0) return [];

    const namesById = new Map(
      promotedEvents.map((event) => [String(event.id), event.name])
    );

    return postEventIds.map((id) => namesById.get(String(id)) || `Event ${id}`);
  }, [editedPost?.event_ids, promotedEvents]);

  // Initialize Uploadcare widget for media upload (only for draft posts)
  useEffect(() => {
    if (!isOpen) return;
    // Only initialize widget if post is in draft status
    if (post && post.status !== "draft") return;

    const attachWidgetHandler = () => {
      if (typeof uploadcare === 'undefined') {
        return;
      }

      try {
        // Get the widget instance
        const widget = uploadcare.SingleWidget('#edit-post-uploadcare-widget-input');

        if (widget && typeof widget.onChange === 'function') {
          uploadcareWidgetRef.current = widget;

          widget.onChange((file) => {
            if (file) {
              setIsUploading(true);
              file.done((fileInfo) => {
                const cdnUrl = fileInfo.cdnUrl || fileInfo.url;
                const mimeType = (fileInfo.mimeType || '').toLowerCase();

                // If mimeType is empty, try to determine from URL extension as fallback
                let finalMimeType = mimeType;
                if (!mimeType && cdnUrl) {
                  const urlLower = cdnUrl.toLowerCase();
                  if (urlLower.match(/\.(mp4|mov|avi|wmv|flv|webm|mkv)$/)) {
                    finalMimeType = 'video/mp4'; // Default video type
                  } else if (urlLower.match(/\.(jpg|jpeg|png|gif|webp|bmp|avif)$/)) {
                    finalMimeType = 'image/jpeg'; // Default image type
                  }
                }

                // Basic validation: must be either video or image (reject PDF, documents, etc.)
                const isVideo = finalMimeType.startsWith('video/');
                const isImage = finalMimeType.startsWith('image/');

                // Only reject if we have a mimeType and it's neither video nor image
                if (finalMimeType && !isVideo && !isImage) {
                  console.error('Unsupported file type:', finalMimeType);
                  toast.error('Please upload an image or video file only (PDF and other document types are not allowed).');
                  if (uploadcareWidgetRef.current) {
                    uploadcareWidgetRef.current.value(null);
                  }
                  setIsUploading(false);
                  return;
                }

                // Use determined mimeType or default to image/jpeg
                const mediaType = isVideo ? 'video' : (finalMimeType || 'image/jpeg');

                setEditedPost(prev => ({
                  ...prev,
                  image: cdnUrl,
                  media_url: cdnUrl,
                  media_type: mediaType,
                  // Per-channel images are images only; a video goes to every channel.
                  ...(isVideo ? { channel_media: {} } : {})
                }));

                setMediaPreview(cdnUrl);
                setIsUploading(false);
                setHasChanges(true);
              }).fail((error) => {
                console.error('Upload failed:', error);
                setIsUploading(false);
              });
            } else {
              // Media removed
              setEditedPost(prev => ({
                ...prev,
                image: null,
                media_url: '',
                media_type: ''
              }));
              setMediaPreview(null);
              setHasChanges(true);
            }
          });
        }
      } catch (error) {
        console.error('Error configuring widget:', error);
      }
    };

    // Wait for Uploadcare to be ready
    const checkReady = setInterval(() => {
      if (typeof uploadcare !== 'undefined' && uploadcare.SingleWidget) {
        clearInterval(checkReady);
        setTimeout(attachWidgetHandler, 100);
      }
    }, 100);

    // Also listen for uploadcare:ready event
    const handleUploadcareReady = () => {
      clearInterval(checkReady);
      setTimeout(attachWidgetHandler, 100);
    };
    document.addEventListener('uploadcare:ready', handleUploadcareReady);

    return () => {
      clearInterval(checkReady);
      document.removeEventListener('uploadcare:ready', handleUploadcareReady);
      uploadcareWidgetRef.current = null;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!post) {
      lastInitializedPostIdRef.current = null;
      setEditedPost({});
      setOriginalPost(null);
      setHasChanges(false);
      setMediaPreview(null);
      return;
    }

    // Only initialize when we actually switch to a different post (by id).
    // This prevents overwriting `editedPost` while the user is interacting.
    const postId = post?.id != null ? String(post.id) : null;
    if (lastInitializedPostIdRef.current === postId) return;
    lastInitializedPostIdRef.current = postId;

    // Store original post for comparison
    setOriginalPost({
      ...post,
      social_media_post_targets: post.social_media_post_targets || inferTargetsFromPlatforms(post.platforms)
    });

    // Set initial media preview
    if (post.image || post.media_url) {
      setMediaPreview(post.image || post.media_url);
    } else {
      setMediaPreview(null);
    }

    // Format the date and time for datetime-local input
    let formattedDateTime = '';

    // Determine which date to use based on post properties
    const tz = (post?.event && post.event.timezone) || post?.timezone || 'UTC';
    if (post.published_at) {
      // If post is published, use published_at date
      formattedDateTime = toLocalInputForTz(post.published_at, tz);
    } else if (post.scheduled_at) {
      // If post has scheduled_at from the database, use it
      formattedDateTime = toLocalInputForTz(post.scheduled_at, tz);
    } else if (post.date) {
      // If post has a scheduled date field, use it
      formattedDateTime = toLocalInputForTz(post.date, tz);
    } else if (post.time) {
      // If post has only time field, combine with current date
      // This is a fallback for the existing data structure
      try {
        const timeStr = post.time.toString();
        let hours = 0;
        let minutes = 0;

        // Handle different time formats (8:00 am, 8:00, etc.)
        if (timeStr.includes(':')) {
          const timeParts = timeStr.split(':');
          hours = parseInt(timeParts[0], 10);

          // Extract minutes, removing any am/pm suffix
          const minutesPart = timeParts[1].replace(/\s*(am|pm)\s*/i, '');
          minutes = parseInt(minutesPart, 10);

          // Adjust for PM if needed
          if (timeStr.toLowerCase().includes('pm') && hours < 12) {
            hours += 12;
          }
          // Adjust for AM 12 (midnight)
          if (timeStr.toLowerCase().includes('am') && hours === 12) {
            hours = 0;
          }
        }

        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);
        formattedDateTime = toLocalInputForTz(date, tz);
      } catch (error) {
        console.error("Error parsing time:", error);
        // Fallback to current time
        formattedDateTime = toLocalInputForTz(new Date(), tz);
      }
    } else {
      // Default to current time if no time information is available
      formattedDateTime = toLocalInputForTz(new Date(), tz);
    }

    setEditedPost({
      ...post,
      scheduledDateTime: formattedDateTime,
      social_media_post_targets: post.social_media_post_targets || inferTargetsFromPlatforms(post.platforms)
    });
    setEditSocialMediaPostTargets(post.social_media_post_targets || inferTargetsFromPlatforms(post.platforms));
    setEditTargetAccounts(post.target_accounts || post.resolved_target_accounts || []);
    setHasChanges(false);
  }, [post?.id]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedPost(prev => {
      if (name === 'social_media_post_targets' || name === 'edit_social_media_post_targets') {
        const platforms =
          value === 'both' ? ['facebook', 'instagram'] :
          value === 'instagram' ? ['instagram'] :
          ['facebook'];

        return {
          ...prev,
          social_media_post_targets: value,
          platforms
        };
      }

      return {
        ...prev,
        [name]: value
      };
    });
    if (name === 'social_media_post_targets' || name === 'edit_social_media_post_targets') {
      setEditSocialMediaPostTargets(value);
    }
    setHasChanges(true);
  };

  const checkForChanges = () => {
    if (!originalPost || !editedPost) return false;

    // Check if description changed
    if (originalPost.description !== editedPost.description) return true;

    // Check if post targeting changed
    if (originalPost.social_media_post_targets !== editedPost.social_media_post_targets) return true;

    // Check if scheduled date/time changed
    if (originalPost.scheduledDateTime !== editedPost.scheduledDateTime) return true;

    // Check if media changed
    const originalMedia = originalPost.image || originalPost.media_url || '';
    const editedMedia = editedPost.image || editedPost.media_url || '';
    if (originalMedia !== editedMedia) return true;

    // Check if media_type changed
    const originalMediaType = originalPost.media_type || '';
    const editedMediaType = editedPost.media_type || '';
    if (originalMediaType !== editedMediaType) return true;

    return false;
  };

  const handleClose = () => {
    if (hasChanges && post && post.status === 'scheduled') {
      if (confirm("You have unsaved changes. Are you sure you want to close without saving?")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const isFutureMinute = () => {
    if (!editedPost || !editedPost.scheduledDateTime) return false;
    const tz = (editedPost?.event && editedPost.event.timezone) || editedPost?.timezone || 'UTC';

    // Compare entirely within the event timezone (no UTC conversion)
    const scheduledLocal = moment.tz(editedPost.scheduledDateTime, 'YYYY-MM-DDTHH:mm', tz);
    const nowLocal = moment.tz(new Date(), tz);
    return scheduledLocal.isAfter(nowLocal, 'minute');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(editedPost, "update");
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="edit-post-header">
          <h2>Edit Post</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Media Upload Section - enabled for every unpublished post */}
            <div className="form-group">
              <label>Update Image or Video</label>
              {isEditable && (
                <>
                  <input
                    type="hidden"
                    role="uploadcare-uploader"
                    name="media"
                    id="edit-post-uploadcare-widget-input"
                    data-images-only="false"
                    data-accept="image/*,video/*"
                  />
                  {isUploading && <small className="text-muted">Uploading...</small>}
                  {!mediaPreview && (
                    <small className="text-muted">Click the upload button above to add or replace media</small>
                  )}
                </>
              )}
              {(mediaPreview || isEditable) && (
                <ChannelImagePreviews
                  mainUrl={mediaPreview}
                  mainType={editedPost.media_type}
                  channelMedia={editedPost.channel_media || {}}
                  targets={editSocialMediaPostTargets}
                  editable={isEditable && !isUploading}
                  onChannelMediaChange={(channelMedia) => {
                    setEditedPost((prev) => ({ ...prev, channel_media: channelMedia }));
                    setHasChanges(true);
                  }}
                  onInstagramStatusChange={setIgStatus}
                />
              )}
            </div>
            <div className="form-group">
              {editedPost?.post_type === 'roundup_post' && (
                <div style={{ marginBottom: 16 }}>
                  <label>Events Included</label>
                  {includedEventNames.length > 0 ? (
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                      {includedEventNames.map((name, index) => (
                        <li key={`${name}-${index}`}>{name}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted" style={{ marginTop: 8, marginBottom: 0 }}>
                      No events included
                    </p>
                  )}
                </div>
              )}

              <label>Post Content</label>
              <textarea
                name="description"
                value={editedPost.description || ""}
                onChange={handleChange}
                className="form-control description"
                rows="6"
              />
            </div>

            {(post?.status === 'failed' || post?.status === 'partially_published') && (
              <div className="form-group" style={{ marginTop: 8 }}>
                <label>Failure reason</label>
                {Array.isArray(post?.platform_statuses) && post.platform_statuses.some(ps => ps?.status === 'failed' && ps?.failure_reason) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {post.platform_statuses
                      .filter(ps => ps?.status === 'failed' && ps?.failure_reason)
                      .map((ps, idx) => (
                        <div
                          key={`${ps.platform || 'platform'}-${idx}`}
                          style={{
                            fontSize: '13px',
                            color: '#dc3545',
                            backgroundColor: '#fff5f5',
                            borderRadius: '4px',
                            padding: '10px',
                            border: '1px solid #fecaca'
                          }}
                        >
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>{(ps.platform || 'platform').toString().toUpperCase()}</div>
                          <div>{ps.failure_reason}</div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#6b7280',
                      padding: '10px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '4px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    No failure details were recorded.
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Post on</label>
              <SocialAccountPicker
                name="edit_target_accounts"
                accounts={socialAccounts}
                selected={editTargetAccounts}
                onChange={handleTargetAccountsChange}
                disabled={!isEditable}
                loading={socialAccountsLoading}
                error={socialAccountsError}
              />
            </div>

            {/* Date picker for every unpublished post. No `min` attribute: a past value (e.g. an
                agent post that landed at now+1min) would make the input invalid and block the
                form, which is exactly the "can't move it" bug. Future-time is enforced on
                Approve / re-save of an approved post instead (see handleSave). */}
            {showsSchedulePicker(post) && (
              <div className="form-group">
              <label>Scheduled Date & Time:</label>
                <input
                  type="datetime-local"
                  name="scheduledDateTime"
                  placeholder="Choose A Date and Time"
                  className="form-control bg-white"
                  value={editedPost.scheduledDateTime || ""}
                  onChange={handleChange}
                />
                <small className="text-muted">
                  {post?.status === 'scheduled'
                    ? 'Saving moves the approved post to this time (must be in the future)'
                    : 'Approving requires a time in the future'}
                </small>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {/* Button logic based on post status */}
            {post && post.status === "draft" ? (
              // Draft state: Save Changes, Approve, Delete, Close
              <>
                {hasChanges && (
                  <button
                    type="submit"
                    className="btns btn-save-changes"
                    onClick={(e) => {
                      e.preventDefault();
                      onSave(editedPost, "update");
                    }}
                  >
                    Save Changes
                  </button>
                )}

                {isFutureMinute() && (
                  <button
                    type="button"
                    className="btns btn-approve"
                    disabled={isUploading || noAccountPicked || instagramBlocksApproval(editSocialMediaPostTargets, mediaPreview, editedPost.media_type, igStatus)}
                    title={noAccountPicked
                      ? 'Pick at least one account to post to'
                      : instagramBlocksApproval(editSocialMediaPostTargets, mediaPreview, editedPost.media_type, igStatus)
                        ? (igStatus === 'error' ? 'Fix the Instagram image before approving' : 'Preparing the Instagram version…')
                        : undefined}
                    onClick={() => {
                      const updatedPost = {
                        ...editedPost,
                        status: "scheduled",
                      };
                      onSave(updatedPost, "schedule");
                    }}
                  >
                    Approve
                  </button>
                )}

                <button
                  type="button"
                  className="btns btn-delete"
                  onClick={() => {
                    onDelete(post.id);
                    onClose();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 512 512" fill="none">
                    <path
                      d="M436 60h-89.185l-9.75-29.238A44.945 44.945 0 0 0 294.379 0h-76.758a44.975 44.975 0 0 0-42.7 30.762L165.182 60H76c-24.814 0-45 20.186-45 45v30c0 16.708 15.041 15 31.183 15H466c8.291 0 15-6.709 15-15v-30c0-24.814-20.186-45-45-45zm-239.187 0 6.57-19.746A14.996 14.996 0 0 1 217.621 30h76.758c6.46 0 12.188 4.116 14.224 10.254L315.18 60H196.813zM64.666 182l23.917 289.072C90.707 494.407 109.97 512 133.393 512h245.215c23.423 0 42.686-17.593 44.824-41.06L447.336 182H64.666zM181 437c0 19.773-30 19.854-30 0V227c0-19.773 30-19.854 30 0v210zm90 0c0 19.773-30 19.854-30 0V227c0-19.773 30-19.854 30 0v210zm90 0c0 19.773-30 19.854-30 0V227c0-8.291 6.709-15 15-15s15 6.709 15 15v210z"
                      fill="#ffffff"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  className="btns btn-close"
                  onClick={handleClose}
                >
                  Close
                </button>
              </>
            ) : post && post.status === "scheduled" ? (
              // Approved/Scheduled state: Save Changes (if edited), Delete, Close
              <>
                {hasChanges && (
                  <button
                    type="submit"
                    className="btns btn-save-changes"
                    onClick={(e) => {
                      e.preventDefault();
                      onSave(editedPost, "update");
                    }}
                  >
                    Save Changes
                  </button>
                )}

                <button
                  type="button"
                  className="btns btn-delete"
                  onClick={() => {
                    onDelete(post.id);
                    onClose();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 512 512" fill="none">
                    <path
                      d="M436 60h-89.185l-9.75-29.238A44.945 44.945 0 0 0 294.379 0h-76.758a44.975 44.975 0 0 0-42.7 30.762L165.182 60H76c-24.814 0-45 20.186-45 45v30c0 16.708 15.041 15 31.183 15H466c8.291 0 15-6.709 15-15v-30c0-24.814-20.186-45-45-45zm-239.187 0 6.57-19.746A14.996 14.996 0 0 1 217.621 30h76.758c6.46 0 12.188 4.116 14.224 10.254L315.18 60H196.813zM64.666 182l23.917 289.072C90.707 494.407 109.97 512 133.393 512h245.215c23.423 0 42.686-17.593 44.824-41.06L447.336 182H64.666zM181 437c0 19.773-30 19.854-30 0V227c0-19.773 30-19.854 30 0v210zm90 0c0 19.773-30 19.854-30 0V227c0-19.773 30-19.854 30 0v210zm90 0c0 19.773-30 19.854-30 0V227c0-8.291 6.709-15 15-15s15 6.709 15 15v210z"
                      fill="#ffffff"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  className="btns btn-close"
                  onClick={handleClose}
                >
                  Close
                </button>
              </>
            ) : (
              // Fallback for other states: Delete, Close
              <>
                <button
                  type="button"
                  className="btns btn-delete"
                  onClick={() => {
                    onDelete(post.id);
                    onClose();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 512 512" fill="none">
                    <path
                      d="M436 60h-89.185l-9.75-29.238A44.945 44.945 0 0 0 294.379 0h-76.758a44.975 44.975 0 0 0-42.7 30.762L165.182 60H76c-24.814 0-45 20.186-45 45v30c0 16.708 15.041 15 31.183 15H466c8.291 0 15-6.709 15-15v-30c0-24.814-20.186-45-45-45zm-239.187 0 6.57-19.746A14.996 14.996 0 0 1 217.621 30h76.758c6.46 0 12.188 4.116 14.224 10.254L315.18 60H196.813zM64.666 182l23.917 289.072C90.707 494.407 109.97 512 133.393 512h245.215c23.423 0 42.686-17.593 44.824-41.06L447.336 182H64.666zM181 437c0 19.773-30 19.854-30 0V227c0-19.773 30-19.854 30 0v210zm90 0c0 19.773-30 19.854-30 0V227c0-19.773 30-19.854 30 0v210zm90 0c0 19.773-30 19.854-30 0V227c0-8.291 6.709-15 15-15s15 6.709 15 15v210z"
                      fill="#ffffff"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  className="btns btn-close"
                  onClick={handleClose}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const CreatePostModal = ({ isOpen, onClose, onSave, promotedEvents, alertConfigurations }) => {
  const [postData, setPostData] = useState({
    event_id: '',
    description: '',
    media_url: '',
    media_type: '',
    scheduled_at: ''
  });
  const [mediaPreview, setMediaPreview] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [channelMedia, setChannelMedia] = useState({});
  // "Post on" starts as the company's default accounts. Left untouched, the post keeps following
  // the default; once changed, the exact pick is saved on the post.
  const { accounts: socialAccounts, defaultKeys, loading: socialAccountsLoading, error: socialAccountsError } = useSocialPostAccounts(isOpen);
  const [pickedAccounts, setPickedAccounts] = useState(null); // null = use the default
  const selectedAccounts = pickedAccounts || defaultKeys;
  const defaultTargets = targetsForAccountKeys(selectedAccounts);
  const uploadcareWidgetRef = React.useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setPostData({
        event_id: '',
        description: '',
        media_url: '',
        media_type: '',
        scheduled_at: ''
      });
      setMediaPreview(null);
      setSelectedEvent(null);
      setIsUploading(false);
      setValidationErrors([]);

      setChannelMedia({});
      setPickedAccounts(null);
      // Clear uploadcare widget
      if (uploadcareWidgetRef.current) {
        uploadcareWidgetRef.current.value(null);
      }
    }
  }, [isOpen]);

  // Initialize Uploadcare widget
  useEffect(() => {
    if (!isOpen) return;

    const attachWidgetHandler = () => {
      if (typeof uploadcare === 'undefined') {
        return;
      }

      try {
        // Get the widget instance
        const widget = uploadcare.SingleWidget('#uploadcare-widget-input');

        if (widget && typeof widget.onChange === 'function') {
          uploadcareWidgetRef.current = widget;

          widget.onChange((file) => {
            if (file) {
              setIsUploading(true);
              file.done((fileInfo) => {
                const cdnUrl = fileInfo.cdnUrl || fileInfo.url;
                const mimeType = (fileInfo.mimeType || '').toLowerCase();

                // If mimeType is empty, try to determine from URL extension as fallback
                let finalMimeType = mimeType;
                if (!mimeType && cdnUrl) {
                  const urlLower = cdnUrl.toLowerCase();
                  if (urlLower.match(/\.(mp4|mov|avi|wmv|flv|webm|mkv)$/)) {
                    finalMimeType = 'video/mp4'; // Default video type
                  } else if (urlLower.match(/\.(jpg|jpeg|png|gif|webp|bmp|avif)$/)) {
                    finalMimeType = 'image/jpeg'; // Default image type
                  }
                }

                // Basic validation: must be either video or image (reject PDF, documents, etc.)
                const isVideo = finalMimeType.startsWith('video/');
                const isImage = finalMimeType.startsWith('image/');

                // Only reject if we have a mimeType and it's neither video nor image
                if (finalMimeType && !isVideo && !isImage) {
                  console.error('Unsupported file type:', finalMimeType);
                  toast.error('Please upload an image or video file only (PDF and other document types are not allowed).', alertConfigurations);
                  if (uploadcareWidgetRef.current) {
                    uploadcareWidgetRef.current.value(null);
                  }
                  setIsUploading(false);
                  return;
                }

                const mediaType = isVideo ? 'video' : (finalMimeType || 'image/jpeg');

                if (isVideo) setChannelMedia({}); // per-channel images are images only
                setPostData(prev => ({
                  ...prev,
                  media_url: cdnUrl,
                  media_type: mediaType
                }));

                setMediaPreview(cdnUrl);
                setIsUploading(false);
              }).fail((error) => {
                console.error('Upload failed:', error);
                toast.error('Failed to upload media. Please try again.', alertConfigurations);
                setIsUploading(false);
              });
            } else {
              setPostData(prev => ({
                ...prev,
                media_url: '',
                media_type: ''
              }));
              setChannelMedia({});
              setMediaPreview(null);
            }
          });
        }
      } catch (error) {
        console.error('Error configuring widget:', error);
      }
    };

    // Wait for Uploadcare to be ready
    const checkReady = setInterval(() => {
      if (typeof uploadcare !== 'undefined' && uploadcare.SingleWidget) {
        clearInterval(checkReady);
        setTimeout(attachWidgetHandler, 100);
      }
    }, 100);

    // Also listen for uploadcare:ready event
    const handleUploadcareReady = () => {
      clearInterval(checkReady);
      setTimeout(attachWidgetHandler, 100);
    };
    document.addEventListener('uploadcare:ready', handleUploadcareReady);

    return () => {
      clearInterval(checkReady);
      document.removeEventListener('uploadcare:ready', handleUploadcareReady);
      uploadcareWidgetRef.current = null;
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPostData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update selected event when event_id changes
    if (name === 'event_id') {
      const event = promotedEvents.find(e => e.id.toString() === value);
      setSelectedEvent(event || null);
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Validate all required fields
    const errors = [];

    if (!postData.event_id) {
      errors.push('Please select an event');
    }

    if (!postData.description || !postData.description.trim()) {
      errors.push('Please enter post content');
    }

    if (!postData.media_url) {
      errors.push('Please upload an image or video');
    }

    if (!postData.scheduled_at) {
      errors.push('Please select a scheduled date and time');
    }

    if (socialAccounts.length > 0 && selectedAccounts.length === 0) {
      errors.push('Please pick at least one account to post to');
    }

    // Show all errors at once
    if (errors.length > 0) {
      setValidationErrors(errors);
      // Also show toast with higher z-index
      toast.error(`Please fill in all required fields: ${errors.join(', ')}`, {
        ...alertConfigurations,
        style: { zIndex: 10001 }
      });
      return;
    }

    // Clear validation errors if validation passes
    setValidationErrors([]);

    // Convert scheduled_at to UTC if provided
    let scheduledAtUTC = null;
    if (postData.scheduled_at && selectedEvent) {
      const eventTimezone = selectedEvent.timezone || 'UTC';
      const localDateTime = moment.tz(postData.scheduled_at, 'YYYY-MM-DDTHH:mm', eventTimezone);
      scheduledAtUTC = localDateTime.utc().toISOString();
    }

    const postDataToSave = {
      ...postData,
      channel_media: channelMedia,
      scheduled_at: scheduledAtUTC,
      // Only a changed pick is saved; untouched posts follow the company default.
      ...(pickedAccounts ? { target_accounts: pickedAccounts } : {})
    };

    onSave(postDataToSave);
  };

  const handleClose = () => {
    if (postData.description || postData.media_url || postData.event_id) {
      if (confirm("You have unsaved changes. Are you sure you want to close?")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  const eventTimezone = selectedEvent?.timezone || 'UTC';
  const minDateTime = nowLocalInputForTz(eventTimezone);

  return (
    <div className="modal-overlay">
      <div className="modal-container create-post-modal">
        <div className="edit-post-header">
          <h2>Create a Post</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="alert alert-danger validation-errors">
                <strong>Please fill in all required fields:</strong>
                <ul>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Event Selection */}
            <div className="form-group">
              <label>Select Event <span className="required-field-indicator">*</span></label>
              <select
                name="event_id"
                value={postData.event_id}
                onChange={handleChange}
                className="form-control"
                required
              >
                <option value="">-- Select an Event --</option>
                {promotedEvents.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
              {promotedEvents.length === 0 && (
                <small className="text-muted required-field-indicator">
                  No promoted events available. Please promote an event first.
                </small>
              )}
            </div>

            {/* Media Upload */}
            <div className="form-group">
              <label>Upload Image or Video</label>
              <input
                type="hidden"
                role="uploadcare-uploader"
                name="media"
                id="uploadcare-widget-input"
                data-images-only="false"
                data-accept="image/*,video/*"
              />
              {isUploading && <small className="text-muted">Uploading...</small>}
              <ChannelImagePreviews
                mainUrl={mediaPreview}
                mainType={postData.media_type}
                channelMedia={channelMedia}
                targets={defaultTargets}
                editable={!isUploading}
                onChannelMediaChange={setChannelMedia}
              />
            </div>

            {/* Post Content */}
            <div className="form-group">
              <label>Post Content <span className="required-field-indicator">*</span></label>
              <textarea
                name="description"
                value={postData.description}
                onChange={handleChange}
                className="form-control description"
                rows="6"
                placeholder="Write your post content here..."
                required
              />
            </div>

            <div className="form-group">
              <label>Post on</label>
              <SocialAccountPicker
                name="create_target_accounts"
                accounts={socialAccounts}
                selected={selectedAccounts}
                onChange={setPickedAccounts}
                loading={socialAccountsLoading}
                error={socialAccountsError}
              />
            </div>

            {/* Scheduled Date & Time */}
            <div className="form-group">
              <label>Scheduled Date & Time</label>
              <input
                type="datetime-local"
                name="scheduled_at"
                value={postData.scheduled_at}
                onChange={handleChange}
                className="form-control bg-white"
                min={minDateTime}
              />
              <small className="text-muted">Schedule must be in the future</small>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="submit"
              className="btns btn-save-changes"
            >
              Save as Draft
            </button>
            <button
              type="button"
              className="btns btn-close"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MetaAuthHelperDisclosure = () => (
  <details className="meta-auth-helper-disclosure">
    <summary>
      <span className="meta-auth-helper-shield" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m9 12 2 2 4-4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span className="meta-auth-helper-summary-text">
        You&apos;ll log in with your personal Facebook account — that&apos;s expected.
      </span>
      <span className="meta-auth-helper-chevron" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="m6 9 6 6 6-6" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </summary>
    <div className="meta-auth-helper-body">
      To connect a Facebook Page, Meta requires you to log in with the personal Facebook account that manages that Page.{' '}
      <strong>Vesta will not post to your personal profile.</strong>{' '}
      This login is only used so you can choose which business Page or connected Instagram account to authorize and post to.
    </div>
  </details>
);

// ── "Accounts & Pages" building blocks ──────────────────────────────────────────────────────
// Presentational only. All state, fetching and mutation live in SettingsModal below; these
// render what they are handed so the list can be reasoned about (and the pure selection logic
// in utils/connectionSelection.js unit-tested) without a DOM.

const AP_PLATFORM_ICON = {
  facebook: '/assets/icons/facebook-icon.png',
  instagram: '/assets/icons/insta-icon.png',
};

const PlatformMark = ({ platform, expired }) => (
  <span className={`ap-mark${expired ? ' is-expired' : ''}`} aria-hidden="true">
    <img src={AP_PLATFORM_ICON[platform] || AP_PLATFORM_ICON.facebook} alt="" width="28" height="28" />
  </span>
);

// A single-line name that shows a styled tooltip ONLY when the text is actually clipped.
// `title` stays on the element as the accessibility/no-JS fallback; the bubble is the visual one.
const ClippedName = ({ text, className }) => {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // The whole point: a name that fits gets no tooltip at all.
    if (el.scrollWidth <= el.clientWidth) return;
    const rect = el.getBoundingClientRect();
    setTip({ left: rect.left + (rect.width / 2), top: rect.top - 8 });
  }, []);

  const hide = useCallback(() => setTip(null), []);

  return (
    <React.Fragment>
      <span
        ref={ref}
        className={className}
        title={text}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {text}
      </span>
      {tip && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div className="ap-name-tip" role="tooltip" style={{ left: tip.left, top: tip.top }}>{text}</div>,
        document.body
      )}
    </React.Fragment>
  );
};

const ConnectionRow = ({ row, selected, onSelect, onRemove, onToggleActive, onReauthenticate, toggling }) => {
  const className = ['ap-row',
    selected ? 'is-selected' : '',
    row.active ? 'is-active' : '',
    row.expired ? 'is-expired' : ''].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <input
        type="checkbox"
        className="ap-check"
        checked={selected}
        onChange={() => onSelect(row.key)}
        aria-label={`Select ${row.name}`}
      />
      <PlatformMark platform={row.platform} expired={row.expired} />
      <span className="ap-rowtext">
        <ClippedName className="ap-rowname" text={row.name} />
        <span className="ap-rowmeta">{row.expired ? `${row.meta} · Token expired` : row.meta}</span>
      </span>

      {row.expired && row.standAlone && (
        <button type="button" className="ap-textbtn" onClick={() => onReauthenticate?.()}>
          Re-authenticate
        </button>
      )}

      {/* Accounts carry a static status pill — there is nothing to toggle on a login. */}
      {row.type === FACEBOOK_ACCOUNT && (
        row.connected ? (
          <span className="ap-pill ap-pill--connected"><span className="ap-pill-dot" />Connected</span>
        ) : (
          <span className="ap-pill ap-pill--setactive">Reconnect needed</span>
        )
      )}

      {/* Pages and Instagram accounts start Active. Unticking stops posting there without
          disconnecting it; several can be on at once. */}
      {row.canToggle && (
        <label
          className={`ap-active${row.active ? ' is-on' : ''}${toggling ? ' is-saving' : ''}`}
          title={row.active
            ? 'Vesta posts here. Untick to stop posting here without disconnecting it.'
            : 'Vesta does not post here. Tick to start posting here again.'}
        >
          <input
            type="checkbox"
            className="ap-check"
            checked={row.active}
            disabled={toggling}
            onChange={() => onToggleActive(row)}
            aria-label={`Post to ${row.name}`}
          />
          {toggling ? 'Saving…' : 'Active'}
        </label>
      )}

      <button
        type="button"
        className="ap-x"
        onClick={() => onRemove(row)}
        title={`Remove ${row.name}`}
        aria-label={`Remove ${row.name}`}
      >
        ✕
      </button>
    </div>
  );
};

const ConnectionsEmptyState = ({ title, body, ctaLabel, onCta }) => (
  <div className="ap-empty">
    <h4>{title}</h4>
    <p>{body}</p>
    <button type="button" className="ap-btn ap-btn--primary" onClick={onCta}>{ctaLabel}</button>
  </div>
);

// THE confirmation. Both the per-row ✕ and the bulk action bar open this same dialog with an
// already-expanded, already-deduped item list — there is never a second confirm behind it, and
// never a window.confirm/alert.
const ConfirmRemoveDialog = ({ items, cascadedPageCount, busy, onCancel, onConfirm }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const count = items.length;

  return (
    <div className="ap-confirm-overlay" onClick={busy ? undefined : onCancel}>
      <div
        className="ap-confirm"
        role="dialog"
        aria-modal="true"
        aria-label={confirmTitle(count)}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{confirmTitle(count)}</h3>
        <p>{confirmBody(count)}</p>

        <div className="ap-confirm-list">
          {items.map((row) => (
            <div className="ap-confirm-item" key={row.key}>
              <PlatformMark platform={row.platform} />
              <span className="ap-confirm-item-name">{row.name}</span>
              <span className="ap-confirm-item-meta">{row.meta}</span>
            </div>
          ))}
        </div>

        {cascadedPageCount > 0 && (
          <div className="ap-confirm-note">{cascadeNote(cascadedPageCount)}</div>
        )}

        <div className="ap-confirm-actions">
          <button type="button" className="ap-btn ap-btn--quiet" onClick={onCancel} disabled={busy}>
            Keep them
          </button>
          <button type="button" className="ap-btn ap-btn--danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Removing…' : removeCtaLabel(count)}
          </button>
        </div>
      </div>
    </div>
  );
};

// The platform chooser that sits in front of the OAuth popups. `mode` decides which of the three
// authorize endpoints are on offer — every one of them stays reachable.
const PlatformChooser = ({ mode, onPick, onCancel }) => (
  <div className="ap-confirm-overlay" onClick={onCancel}>
    <div className="ap-confirm" style={{ maxWidth: 400 }} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
      <h3>{mode === 'account' ? 'Connect an account' : 'Add a Page or profile'}</h3>
      <p>
        {mode === 'account'
          ? 'Choose the login Vesta should use to reach your Pages and profiles.'
          : 'Choose where the new Page or profile is authorized from.'}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        <button type="button" className="ap-btn ap-btn--outline" onClick={() => onPick('facebook')}>
          {mode === 'account' ? 'Facebook' : 'Facebook Page'}
        </button>
        {mode === 'page' && (
          <button type="button" className="ap-btn ap-btn--outline" onClick={() => onPick('instagram')}>
            Instagram via a Facebook Page
          </button>
        )}
        {mode === 'account' && (
          <button type="button" className="ap-btn ap-btn--outline" onClick={() => onPick('instagram_standalone')}>
            Instagram direct (Business or Creator)
          </button>
        )}
      </div>
      <div className="ap-confirm-actions">
        <button type="button" className="ap-btn ap-btn--quiet" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  </div>
);

const SettingsModal = ({ isOpen, onClose, onPostTargetsSaved }) => {
  // The connection list is read from the API (see loadConnections below), not from the
  // server-rendered DOM attributes — those only refresh on a full page load, which is exactly
  // what this tab no longer does.
  const reactRoot = document.getElementById('react-root');

  const [instructions, setInstructions] = useState('');
  const [mediaAssetsUrl, setMediaAssetsUrl] = useState('');
  const [socialMediaPostTargets, setSocialMediaPostTargets] = useState('both');
  // Post targeting: the company's default accounts. `defaultPick` is the unsaved edit (null = none).
  const {
    accounts: socialAccounts, defaultKeys, loading: socialAccountsLoading, error: socialAccountsError,
    reload: reloadSocialAccounts, saveDefaults: saveDefaultAccounts
  } = useSocialPostAccounts(isOpen);
  const [defaultPick, setDefaultPick] = useState(null);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [activeTab, setActiveTab] = useState('accounts');
  // Whole connection_status payloads, not just their arrays — the merged tab also needs the
  // account-level flags (facebook_account_exists / token_valid), and the bulk-remove response
  // hands both payloads back so state can be replaced wholesale with no re-fetch.
  const [facebookStatus, setFacebookStatus] = useState(null);
  const [instagramStatus, setInstagramStatus] = useState(null);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsError, setConnectionsError] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  // null, or the already-expanded/deduped removal set awaiting THE one confirmation.
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);
  // Post generation moved to Agent Jobs (the n8n content-plan trigger is off), so the old Content
  // Plan sub-tabs are hidden and only Post targeting is reachable. The legacy sub-tab code is kept
  // below, unreachable, until we decide to delete it.
  const [contentPlanSubTab, setContentPlanSubTab] = useState('post-targeting');
  const [triggeringGeneration, setTriggeringGeneration] = useState(false);
  const [contentPlan, setContentPlan] = useState(null);
  const [contentPlanEvents, setContentPlanEvents] = useState([]);
  const [contentPlanLoading, setContentPlanLoading] = useState(false);
  const [prefs, setPrefs] = useState({ generation_day: 1, generation_horizon: 1, posts_per_day: 1 });
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [stage1Prompt, setStage1Prompt] = useState('');
  const [stage1PromptsSaving, setStage1PromptsSaving] = useState(false);
  const [stage2Prompt, setStage2Prompt] = useState('');
  const [stage2PromptsSaving, setStage2PromptsSaving] = useState(false);
  const [stage3Prompt, setStage3Prompt] = useState('');
  const [stage3PromptsSaving, setStage3PromptsSaving] = useState(false);
  const [aiConfigStageTab, setAiConfigStageTab] = useState('stage1');
  const [expandedReportRunIds, setExpandedReportRunIds] = useState(() => new Set());

  const isAdmin = reactRoot?.getAttribute('is-admin') === 'true';

  // Land on the Agent Jobs list, not the board: the activity shell reads its last view from storage.
  const openAgentJobs = () => {
    try { persistActivityView('agents', window.localStorage); } catch (_e) { /* storage blocked */ }
  };

  useEffect(() => {
    if (!isOpen) return;
    const fromDom = reactRoot?.getAttribute('social-media-instructions') || '';
    setInstructions(fromDom);
    const mediaAssetsFromDom = reactRoot?.getAttribute('media-assets-url') || '';
    setMediaAssetsUrl(mediaAssetsFromDom);

    const targetsFromDom = reactRoot?.getAttribute('social-media-post-targets') || 'both';
    setSocialMediaPostTargets(targetsFromDom);
    setDefaultPick(null);
  }, [isOpen]);

  // Load the company's connections for the merged Accounts & Pages tab.
  //
  // The version this replaces swallowed every failure with `.catch(() => {})`, so a 500 or a
  // dropped connection was indistinguishable from "you have nothing connected" — the user was
  // shown an empty state and invited to reconnect something that was already there. Failures now
  // surface as an explicit error with a retry.
  const loadConnections = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setConnectionsLoading(true);
    try {
      const [fbRes, igRes] = await Promise.all([
        fetch('/api/facebook/connection_status', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
        fetch('/api/instagram/connection_status', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
      ]);
      if (!fbRes.ok || !igRes.ok) throw new Error('connection_status request failed');
      const [fbData, igData] = await Promise.all([fbRes.json(), igRes.json()]);
      setFacebookStatus(fbData || {});
      setInstagramStatus(igData || {});
      // Out-of-scope code (the approve-post guard, the ConnectPageModal gate, the dashboard) still
      // reads the #react-root attributes, which nothing refreshes now that we never reload.
      mirrorConnectionAttrs(fbData, igData);
      setConnectionsError(null);
    } catch (_e) {
      setConnectionsError("We couldn't load your connections. Check your connection and try again.");
    } finally {
      if (!quiet) setConnectionsLoading(false);
    }
  }, []);

  // Fetch on modal open (whichever tab is showing) and again whenever the merged tab is entered,
  // so the list is never stale after a connect that happened elsewhere. `connectionsError` is in
  // the guard, not just `facebookStatus`: after a failed first fetch there is no status, so
  // without it every unrelated tab switch would re-fire a spinner-showing load.
  useEffect(() => {
    if (!isOpen) return;
    if (activeTab !== 'accounts' && (facebookStatus || connectionsError)) return;
    loadConnections();
  }, [isOpen, activeTab]);

  // Load content plan and upcoming events when the AI Config (admin) tab is active
  useEffect(() => {
    const needContentPlan = activeTab === 'ai-config' && isAdmin;
    if (!isOpen || !needContentPlan) return;
    setContentPlanLoading(true);
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const headers = { Accept: 'application/json' };
    if (csrf) headers['X-CSRF-Token'] = csrf;
    fetch('/api/content_plan', { headers, credentials: 'same-origin' })
      .then((res) => {
        return res.json().then((data) => ({ ok: res.ok, status: res.status, data }));
      })
      .then(({ ok, data }) => {
        if (ok) {
          if (data.content_plan) {
            setContentPlan({
              ...data.content_plan,
              content_plan_runs: Array.isArray(data.content_plan_runs) ? data.content_plan_runs : []
            });
            setPrefs({
              generation_day: data.content_plan.generation_day ?? 1,
              generation_horizon: data.content_plan.generation_horizon ?? 1,
              posts_per_day: data.content_plan.posts_per_day ?? 1
            });
          }
          if (typeof data.media_assets_url === 'string') {
            setMediaAssetsUrl(data.media_assets_url);
          }
          setContentPlanEvents(Array.isArray(data.events) ? data.events : []);
        } else {
          setContentPlanEvents([]);
          toast.error(data?.error || data?.message || 'Failed to load content plan');
        }
      })
      .catch(() => {
        setContentPlanEvents([]);
        toast.error('Failed to load content plan');
      })
      .finally(() => setContentPlanLoading(false));
  }, [isOpen, activeTab, isAdmin]);

  // Sync Stage 1–3 system prompts from content plan when loaded (for AI Config tab); show readable tokens in UI
  useEffect(() => {
    if (!contentPlan) return;
    if (contentPlan.effective_stage_1_system_prompt != null) setStage1Prompt(contentPlan.effective_stage_1_system_prompt);
    if (contentPlan.effective_stage_2_system_prompt != null) setStage2Prompt(contentPlan.effective_stage_2_system_prompt);
    if (contentPlan.effective_stage_3_system_prompt != null) setStage3Prompt(contentPlan.effective_stage_3_system_prompt);
  }, [
    contentPlan?.id,
    contentPlan?.effective_stage_1_system_prompt,
    contentPlan?.effective_stage_2_system_prompt,
    contentPlan?.effective_stage_3_system_prompt
  ]);

  // Anything that can change connections behind our back settles here: re-read state in place
  // and let the calendar behind the modal quietly re-read too. No reload, so the modal, the tab
  // and the scroll position all survive.
  const handleConnectionsChanged = useCallback(() => {
    loadConnections({ quiet: true });
    reloadSocialAccounts();
    onPostTargetsSaved?.();
  }, [loadConnections, reloadSocialAccounts, onPostTargetsSaved]);

  const openOAuthPopup = useOAuthPopup(handleConnectionsChanged);

  const openFacebookPopup = () => openOAuthPopup('/facebook/oauth/authorize_social_media?popup=1', 'fbAuth');
  const openInstagramPopup = () => openOAuthPopup('/facebook/oauth/authorize_instagram?popup=1', 'igAuth');
  const openInstagramStandalonePopup = () => openOAuthPopup('/facebook/oauth/authorize_instagram_standalone?popup=1', 'igStandaloneAuth');

  const [togglingPageId, setTogglingPageId] = useState(null);

  // Activate / deactivate which Page this company publishes to. Pages connect inactive by
  // default (so we never auto-post to a Page the user didn't choose); this is how the user
  // designates the Page(s) to publish to. Only active Pages are published to.
  // Switches one Facebook Page or Instagram account on/off for posting. Never disconnects it.
  const handleTogglePage = async (row) => {
    const isInstagram = row.type === INSTAGRAM_ACCOUNT;
    const pageId = row.id;
    setTogglingPageId(pageId);
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const url = isInstagram ? `/api/instagram/accounts/${pageId}/toggle` : `/api/facebook/pages/${pageId}/toggle`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
        credentials: 'same-origin',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        toast.error(data.error || 'Failed to update.');
        return;
      }
      // Toggle persisted server-side. Reflect it immediately so the checkbox is never stale...
      const nowActive = isInstagram ? data.account?.active : data.page?.active;
      if (isInstagram) {
        setInstagramStatus((prev) => (prev && Array.isArray(prev.accounts)
          ? { ...prev, accounts: prev.accounts.map((a) => (a.id === pageId ? { ...a, active: nowActive } : a)) }
          : prev));
      } else {
        setFacebookStatus((prev) => (prev && Array.isArray(prev.pages)
          ? { ...prev, pages: prev.pages.map((pg) => (pg.id === pageId ? { ...pg, active: nowActive } : pg)) }
          : prev));
      }
      toast.success(nowActive ? `Vesta will post to ${row.name}.` : `Vesta will stop posting to ${row.name}. It stays connected.`);
      // ...then best-effort refetch to stay in sync. A refetch failure must NOT look like a
      // toggle failure (the toggle already succeeded), so it's isolated in its own try/catch.
      try {
        if (isInstagram) {
          const statusRes = await fetch('/api/instagram/connection_status', { headers: { Accept: 'application/json' } });
          const statusData = await statusRes.json().catch(() => ({}));
          if (statusData && Array.isArray(statusData.accounts)) setInstagramStatus(statusData);
        } else {
          const statusRes = await fetch('/api/facebook/connection_status', { headers: { Accept: 'application/json' } });
          const statusData = await statusRes.json().catch(() => ({}));
          if (statusData && Array.isArray(statusData.pages)) setFacebookStatus(statusData);
        }
      } catch (_e) { /* optimistic state already applied */ }
    } catch (_e) {
      toast.error('Failed to update.');
    } finally {
      setTogglingPageId(null);
    }
  };

  // 'account' | 'page' | null — which set of authorize endpoints the chooser is offering.
  const [choiceMode, setChoiceMode] = useState(null);

  const handleChoice = (type) => {
    setChoiceMode(null);
    if (type === 'facebook') openFacebookPopup();
    else if (type === 'instagram') openInstagramPopup();
    else if (type === 'instagram_standalone') openInstagramStandalonePopup();
  };

  // SettingsModal early-returns null when closed but is rendered unconditionally by the page, so
  // it never unmounts and this state would otherwise outlive the modal. Reopening onto a stale
  // "1 selected" for a row that has since been removed makes Remove selected a silent no-op — the
  // expansion finds nothing and the dialog never opens — so transient UI state is dropped on close.
  useEffect(() => {
    if (isOpen) return;
    setSelectedKeys(new Set());
    setConfirmTarget(null);
    setRemoveError(null);
    setChoiceMode(null);
  }, [isOpen]);

  // ── Rows + selection ──────────────────────────────────────────────────────────────────────
  const accountRows = useMemo(() => buildAccountRows(facebookStatus), [facebookStatus]);
  const pageRows = useMemo(() => buildPageRows(facebookStatus, instagramStatus), [facebookStatus, instagramStatus]);

  const handleSelect = useCallback((key) => {
    setSelectedKeys((prev) => toggleKey(prev, key));
  }, []);

  const selectedCount = selectedKeys.size;

  // Both removal paths funnel through here, so there is exactly ONE confirmation for either.
  // The expansion (account → its Pages) and the dedup happen before the dialog opens, which is
  // why the dialog can just render `items` and never has to reason about cascades.
  const openConfirmFor = useCallback((keys) => {
    const expanded = expandSelection(keys, { accountRows, pageRows });
    if (expanded.items.length === 0) return;
    setConfirmTarget(expanded);
  }, [accountRows, pageRows]);

  const handleRowRemove = useCallback((row) => openConfirmFor([row.key]), [openConfirmFor]);
  const handleBulkRemove = useCallback(() => openConfirmFor(selectedKeys), [openConfirmFor, selectedKeys]);

  // One request, all-or-nothing. The server answers with both connection_status payloads, so
  // state is replaced from the response — no follow-up fetch, no window they could disagree in.
  const performRemoval = useCallback(async () => {
    if (!confirmTarget) return;
    const items = confirmTarget.items;
    setRemoving(true);
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const res = await fetch('/api/facebook/connections', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
        credentials: 'same-origin',
        body: JSON.stringify({ connections: toRemovalPayload(items) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'bulk remove failed');

      if (data.facebook) setFacebookStatus(data.facebook);
      if (data.instagram) setInstagramStatus(data.instagram);
      // Critical after a removal specifically: drop your last Page here and the approve-post guard
      // must stop believing you still have one.
      mirrorConnectionAttrs(data.facebook, data.instagram);
      // Only the rows that actually went lose their tick; anything else stays selected.
      setSelectedKeys((prev) => clearRemovedKeys(prev, items));
      setRemoveError(null);
      setConfirmTarget(null);
      // Count the ROWS that left the list, not the server's `removed`. When an account cascades,
      // the server dedupes its Pages away and reports 1, but the user confirmed "Remove all 3"
      // and watched three rows go — the toast has to agree with the dialog they just clicked.
      toast.success(removalToast(items.length));
      // Scheduled posts pointed at a removed Page are still in the calendar — let it re-read.
      onPostTargetsSaved?.();
    } catch (_e) {
      // Nothing was removed (the server wraps the whole thing in a transaction): the list and
      // the selection both stay exactly as they were, and the failure shows inline.
      setConfirmTarget(null);
      setRemoveError(REMOVAL_ERROR);
    } finally {
      setRemoving(false);
    }
  }, [confirmTarget, onPostTargetsSaved]);

  const handleSave = async () => {
    const companyId = reactRoot?.getAttribute('company-id');
    if (!companyId) {
      toast.error('Company not found.');
      return;
    }

    try {
      const res = await fetch(`/companies/${companyId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({
          company: {
            social_media_instructions: instructions || '',
            media_assets_url: mediaAssetsUrl || '',
            social_media_post_targets: socialMediaPostTargets || 'both'
          }
        })
      });

      // Consider 200, 201 as success; some Rails responses may redirect (302) for HTML – we set Accept above, but be permissive
      if (res.ok || res.status === 201) {
        toast.success('Org settings saved', { position: 'top-right' });
        try { reactRoot?.setAttribute('social-media-instructions', instructions || ''); } catch (_e) {}
        try { reactRoot?.setAttribute('media-assets-url', mediaAssetsUrl || ''); } catch (_e) {}
        try { reactRoot?.setAttribute('social-media-post-targets', socialMediaPostTargets || 'both'); } catch (_e) {}
        onClose?.();
      } else {
        let message = 'Failed to save settings';
        try {
          const contentType = res.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await res.json();
            if (data && (data.error || data.message)) message = data.error || data.message;
          } else {
            const txt = await res.text();
            if (txt) message = txt.substring(0, 200);
          }
        } catch (_e) {}
        toast.error(message);
      }
    } catch (e) {
      toast.error('Failed to save settings');
    }
  };

  const handleTriggerGeneration = async () => {
    setTriggeringGeneration(true);
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    try {
      const res = await fetch('/api/content_plan/trigger', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrf || ''
        },
        credentials: 'same-origin'
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Posts generation in progress.');
      } else {
        toast.error(data.error || 'Generation trigger failed.');
      }
    } catch (e) {
      toast.error('Generation trigger failed.');
    } finally {
      setTriggeringGeneration(false);
    }
  };

  const handleSavePostTargets = async () => {
    const keys = defaultPick || defaultKeys;
    setSavingDefaults(true);
    try {
      const error = await saveDefaultAccounts(keys);
      if (error) {
        toast.error(error);
        return;
      }
      // Keep the older facebook/instagram/both value in step, since Org settings still saves it.
      const targets = targetsForAccountKeys(keys);
      setSocialMediaPostTargets(targets);
      try { reactRoot?.setAttribute('social-media-post-targets', targets); } catch (_e) {}
      setDefaultPick(null);
      toast.success('Post targeting saved', { position: 'top-right' });
      onPostTargetsSaved?.();
    } catch (_e) {
      toast.error('Failed to save post targeting');
    } finally {
      setSavingDefaults(false);
    }
  };

  if (!isOpen) return null;

  return (
    // ai instructions modal
    <div className="modal-overlay settings-page-modal" onClick={onClose}>
      <div className="modal-container connect-page-container" onClick={(e) => e.stopPropagation()}>
        <div className="connect-page-header-ai">
          <h2>Settings</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="connect-page-contents">
          <div className="smai-agent-jobs-note" role="note">
            <span>
              Social media post generation now lives in <strong>Agent Jobs</strong>. Create or edit an
              agent job to choose what Vesta posts and when.
            </span>
            <a href="/activity?mode=agent" onClick={openAgentJobs}>Go to Agent Jobs</a>
          </div>

          <div className="settings-tabs">
            <button
              type="button"
              className={`settings-tab settings-tab--twoline ${activeTab === 'accounts' ? 'active' : ''}`}
              onClick={() => setActiveTab('accounts')}
            >
              {'Accounts &\nPages'}
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'instructions' ? 'active' : ''}`}
              onClick={() => setActiveTab('instructions')}
            >
              AI Instructions
            </button>
            {/* Media Assets is hidden from customers: Agent Jobs replaced the old content plan, but
                the agent still reads a saved media_assets_url, so admins can still see and edit it. */}
            {isAdmin && (
              <button
                type="button"
                className={`settings-tab ${activeTab === 'media-assets' ? 'active' : ''}`}
                onClick={() => setActiveTab('media-assets')}
              >
                Media Assets
              </button>
            )}
            <button
              type="button"
              className={`settings-tab ${activeTab === 'post-targeting' ? 'active' : ''}`}
              onClick={() => setActiveTab('post-targeting')}
            >
              Post Targeting
            </button>
            {isAdmin && (
              <button
                type="button"
                className={`settings-tab ${activeTab === 'ai-config' ? 'active' : ''}`}
                onClick={() => setActiveTab('ai-config')}
              >
                AI Configuration
              </button>
            )}
          </div>

          {activeTab === 'ai-config' && (
            <div className="connect-page-content">
              <h4 style={{ marginBottom: '12px', fontSize: '16px' }}>AI Configuration</h4>
              <p className="text-muted" style={{ marginBottom: '12px' }}>
                System prompts for content generation (Stage 1–3). Saved prompts are stored in this company's content plan.
              </p>
              <div className="settings-tabs" style={{ marginBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                <button
                  type="button"
                  className={`settings-tab ${aiConfigStageTab === 'stage1' ? 'active' : ''}`}
                  onClick={() => setAiConfigStageTab('stage1')}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  Stage 1
                </button>
                <button
                  type="button"
                  className={`settings-tab ${aiConfigStageTab === 'stage2' ? 'active' : ''}`}
                  onClick={() => setAiConfigStageTab('stage2')}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  Stage 2
                </button>
                <button
                  type="button"
                  className={`settings-tab ${aiConfigStageTab === 'stage3' ? 'active' : ''}`}
                  onClick={() => setAiConfigStageTab('stage3')}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  Stage 3
                </button>
              </div>
              {aiConfigStageTab === 'stage1' && (
                <div style={{ marginBottom: '20px' }}>
                  <h5 style={{ marginBottom: '8px', fontSize: '14px' }}>Stage 1 System Prompt</h5>
                  <textarea
                    className="form-control"
                    rows={28}
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
                    placeholder="Loading..."
                    value={stage1Prompt}
                    onChange={(e) => setStage1Prompt(e.target.value)}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        disabled={stage1PromptsSaving || contentPlanLoading}
                        onClick={async () => {
                          setStage1PromptsSaving(true);
                          const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                          try {
                            const res = await fetch('/api/content_plan', {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                              credentials: 'same-origin',
                              body: JSON.stringify({ content_plan: { stage_1_payload: null } })
                            });
                            const data = await res.json().catch(() => ({}));
                            if (res.ok && data.content_plan) {
                              setContentPlan(data.content_plan);
                              setStage1Prompt(data.content_plan.effective_stage_1_system_prompt);
                              toast.success('Stage 1 set to default.');
                            } else {
                              toast.error(data.errors?.[0] || 'Failed to set default.');
                            }
                          } catch (e) {
                            toast.error('Failed to set default.');
                          }
                          setStage1PromptsSaving(false);
                        }}
                        style={{ padding: '8px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#374151' }}
                      >
                        Reset Stage to Default
                      </button>
                      <button
                        type="button"
                        disabled={stage1PromptsSaving || contentPlanLoading}
                        onClick={async () => {
                          setStage1PromptsSaving(true);
                          const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                          try {
                            const res = await fetch('/api/content_plan', {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                              credentials: 'same-origin',
                              body: JSON.stringify({ content_plan: { stage_1_payload: null, stage_2_payload: null, stage_3_payload: null } })
                            });
                            const data = await res.json().catch(() => ({}));
                            if (res.ok && data.content_plan) {
                              setContentPlan(data.content_plan);
                              setStage1Prompt(data.content_plan.effective_stage_1_system_prompt);
                              setStage2Prompt(data.content_plan.effective_stage_2_system_prompt);
                              setStage3Prompt(data.content_plan.effective_stage_3_system_prompt);
                              toast.success('All stages set to default.');
                            } else {
                              toast.error(data.errors?.[0] || 'Failed to reset all.');
                            }
                          } catch (e) {
                            toast.error('Failed to reset all.');
                          }
                          setStage1PromptsSaving(false);
                        }}
                        style={{ padding: '8px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#6b7280' }}
                      >
                        Reset All
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={stage1PromptsSaving || contentPlanLoading}
                      onClick={async () => {
                        setStage1PromptsSaving(true);
                        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                        try {
                          const res = await fetch('/api/content_plan', {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                            credentials: 'same-origin',
                            body: JSON.stringify({ content_plan: { stage_1_payload: stage1Prompt } })
                          });
                          const data = await res.json().catch(() => ({}));
                          if (res.ok && data.content_plan) {
                            setContentPlan(data.content_plan);
                            toast.success('Stage 1 prompt saved.');
                          } else {
                            toast.error(data.errors?.[0] || 'Failed to save prompt.');
                          }
                        } catch (e) {
                          toast.error('Failed to save prompt.');
                        }
                        setStage1PromptsSaving(false);
                      }}
                      style={{ padding: '8px 20px', background: stage1PromptsSaving ? '#9ca3af' : '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: stage1PromptsSaving ? 'not-allowed' : 'pointer' }}
                    >
                      {stage1PromptsSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}
              {aiConfigStageTab === 'stage2' && (
                <div style={{ marginBottom: '20px' }}>
                  <h5 style={{ marginBottom: '8px', fontSize: '14px' }}>Stage 2 System Prompt</h5>
                  <textarea
                    className="form-control"
                    rows={28}
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
                    placeholder="Loading..."
                    value={stage2Prompt}
                    onChange={(e) => setStage2Prompt(e.target.value)}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        disabled={stage2PromptsSaving || contentPlanLoading}
                        onClick={async () => {
                          setStage2PromptsSaving(true);
                          const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                          try {
                            const res = await fetch('/api/content_plan', {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                              credentials: 'same-origin',
                              body: JSON.stringify({ content_plan: { stage_2_payload: null } })
                            });
                            const data = await res.json().catch(() => ({}));
                            if (res.ok && data.content_plan) {
                              setContentPlan(data.content_plan);
                              setStage2Prompt(data.content_plan.effective_stage_2_system_prompt);
                              toast.success('Stage 2 set to default.');
                            } else {
                              toast.error(data.errors?.[0] || 'Failed to set default.');
                            }
                          } catch (e) {
                            toast.error('Failed to set default.');
                          }
                          setStage2PromptsSaving(false);
                        }}
                        style={{ padding: '8px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#374151' }}
                      >
                        Reset Stage to Default
                      </button>
                      <button
                        type="button"
                        disabled={stage2PromptsSaving || contentPlanLoading}
                        onClick={async () => {
                          setStage2PromptsSaving(true);
                          const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                          try {
                            const res = await fetch('/api/content_plan', {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                              credentials: 'same-origin',
                              body: JSON.stringify({ content_plan: { stage_1_payload: null, stage_2_payload: null, stage_3_payload: null } })
                            });
                            const data = await res.json().catch(() => ({}));
                            if (res.ok && data.content_plan) {
                              setContentPlan(data.content_plan);
                              setStage1Prompt(data.content_plan.effective_stage_1_system_prompt);
                              setStage2Prompt(data.content_plan.effective_stage_2_system_prompt);
                              setStage3Prompt(data.content_plan.effective_stage_3_system_prompt);
                              toast.success('All stages set to default.');
                            } else {
                              toast.error(data.errors?.[0] || 'Failed to reset all.');
                            }
                          } catch (e) {
                            toast.error('Failed to reset all.');
                          }
                          setStage2PromptsSaving(false);
                        }}
                        style={{ padding: '8px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#6b7280' }}
                      >
                        Reset All
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={stage2PromptsSaving || contentPlanLoading}
                      onClick={async () => {
                        setStage2PromptsSaving(true);
                        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                        try {
                          const res = await fetch('/api/content_plan', {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                            credentials: 'same-origin',
                            body: JSON.stringify({ content_plan: { stage_2_payload: stage2Prompt } })
                          });
                          const data = await res.json().catch(() => ({}));
                          if (res.ok && data.content_plan) {
                            setContentPlan(data.content_plan);
                            toast.success('Stage 2 prompt saved.');
                          } else {
                            toast.error(data.errors?.[0] || 'Failed to save prompt.');
                          }
                        } catch (e) {
                          toast.error('Failed to save prompt.');
                        }
                        setStage2PromptsSaving(false);
                      }}
                      style={{ padding: '8px 20px', background: stage2PromptsSaving ? '#9ca3af' : '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: stage2PromptsSaving ? 'not-allowed' : 'pointer' }}
                    >
                      {stage2PromptsSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}
              {aiConfigStageTab === 'stage3' && (
                <div style={{ marginBottom: '20px' }}>
                  <h5 style={{ marginBottom: '8px', fontSize: '14px' }}>Stage 3 System Prompt</h5>
                  <textarea
                    className="form-control"
                    rows={28}
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
                    placeholder="Loading..."
                    value={stage3Prompt}
                    onChange={(e) => setStage3Prompt(e.target.value)}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        disabled={stage3PromptsSaving || contentPlanLoading}
                        onClick={async () => {
                          setStage3PromptsSaving(true);
                          const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                          try {
                            const res = await fetch('/api/content_plan', {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                              credentials: 'same-origin',
                              body: JSON.stringify({ content_plan: { stage_3_payload: null } })
                            });
                            const data = await res.json().catch(() => ({}));
                            if (res.ok && data.content_plan) {
                              setContentPlan(data.content_plan);
                              setStage3Prompt(data.content_plan.effective_stage_3_system_prompt);
                              toast.success('Stage 3 set to default.');
                            } else {
                              toast.error(data.errors?.[0] || 'Failed to set default.');
                            }
                          } catch (e) {
                            toast.error('Failed to set default.');
                          }
                          setStage3PromptsSaving(false);
                        }}
                        style={{ padding: '8px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#374151' }}
                      >
                        Reset Stage to Default
                      </button>
                      <button
                        type="button"
                        disabled={stage3PromptsSaving || contentPlanLoading}
                        onClick={async () => {
                          setStage3PromptsSaving(true);
                          const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                          try {
                            const res = await fetch('/api/content_plan', {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                              credentials: 'same-origin',
                              body: JSON.stringify({ content_plan: { stage_1_payload: null, stage_2_payload: null, stage_3_payload: null } })
                            });
                            const data = await res.json().catch(() => ({}));
                            if (res.ok && data.content_plan) {
                              setContentPlan(data.content_plan);
                              setStage1Prompt(data.content_plan.effective_stage_1_system_prompt);
                              setStage2Prompt(data.content_plan.effective_stage_2_system_prompt);
                              setStage3Prompt(data.content_plan.effective_stage_3_system_prompt);
                              toast.success('All stages set to default.');
                            } else {
                              toast.error(data.errors?.[0] || 'Failed to reset all.');
                            }
                          } catch (e) {
                            toast.error('Failed to reset all.');
                          }
                          setStage3PromptsSaving(false);
                        }}
                        style={{ padding: '8px 14px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#6b7280' }}
                      >
                        Reset All
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={stage3PromptsSaving || contentPlanLoading}
                      onClick={async () => {
                        setStage3PromptsSaving(true);
                        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                        try {
                          const res = await fetch('/api/content_plan', {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                            credentials: 'same-origin',
                            body: JSON.stringify({ content_plan: { stage_3_payload: stage3Prompt } })
                          });
                          const data = await res.json().catch(() => ({}));
                          if (res.ok && data.content_plan) {
                            setContentPlan(data.content_plan);
                            toast.success('Stage 3 prompt saved.');
                          } else {
                            toast.error(data.errors?.[0] || 'Failed to save prompt.');
                          }
                        } catch (e) {
                          toast.error('Failed to save prompt.');
                        }
                        setStage3PromptsSaving(false);
                      }}
                      style={{ padding: '8px 20px', background: stage3PromptsSaving ? '#9ca3af' : '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: stage3PromptsSaving ? 'not-allowed' : 'pointer' }}
                    >
                      {stage3PromptsSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'post-targeting' && (
            <div className="connect-page-content" style={{ marginBottom: '1rem' }}>
              {/* Legacy Content Plan sub-tab bar, hidden (see contentPlanSubTab above). */}
              <div className="settings-tabs" hidden style={{ marginBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                <button
                  type="button"
                  className={`settings-tab ${contentPlanSubTab === 'event-selection' ? 'active' : ''}`}
                  onClick={() => setContentPlanSubTab('event-selection')}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  Event Selection
                </button>
                <button
                  type="button"
                  className={`settings-tab ${contentPlanSubTab === 'content-preferences' ? 'active' : ''}`}
                  onClick={() => setContentPlanSubTab('content-preferences')}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  Content Preferences
                </button>
                <button
                  type="button"
                  className={`settings-tab ${contentPlanSubTab === 'generation-history' ? 'active' : ''}`}
                  onClick={() => setContentPlanSubTab('generation-history')}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  Generation History
                </button>
                <button
                  type="button"
                  className={`settings-tab ${contentPlanSubTab === 'post-targeting' ? 'active' : ''}`}
                  onClick={() => setContentPlanSubTab('post-targeting')}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  Post Targeting
                </button>
                <button
                  type="button"
                  className={`settings-tab ${contentPlanSubTab === 'generate-now' ? 'active' : ''}`}
                  onClick={() => setContentPlanSubTab('generate-now')}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  Generate Now
                </button>
              </div>
              {contentPlanSubTab === 'event-selection' && (
                <div className="content-plan-event-selection">
                  {contentPlanLoading ? (
                    <p className="text-muted">Loading...</p>
                  ) : (
                    <>
                      <h4 style={{ marginBottom: '8px', fontSize: '14px' }}>Events in your content plan</h4>
                      <p className="text-muted" style={{ marginBottom: '16px', fontSize: '13px' }}>
                        All upcoming active events are automatically included. Remove events you don't want included in AI-generated posts.
                      </p>
                      {contentPlanEvents.length === 0 ? (
                        <p className="text-muted">No upcoming events found.</p>
                      ) : (
                        <ul className="list-unstyled" style={{ marginBottom: '16px' }}>
                          {contentPlanEvents.map((ev) => {
                            const isRemoved = ev.status === 'removed';
                            return (
                              <li
                                key={ev.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '8px 12px',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                  marginBottom: '8px',
                                  backgroundColor: isRemoved ? '#f9fafb' : '#fff',
                                  opacity: isRemoved ? 0.6 : 1
                                }}
                              >
                                <span>
                                  <strong>{ev.name}</strong>
                                  {ev.start_date && (
                                    <span className="text-muted" style={{ marginLeft: '8px', fontSize: '14px' }}>
                                      {ev.start_date}
                                      {ev.end_date && ev.end_date !== ev.start_date ? ` – ${ev.end_date}` : ''}
                                    </span>
                                  )}
                                  {!isRemoved && (
                                    <span style={{
                                      marginLeft: '8px', fontSize: '12px', padding: '2px 6px', borderRadius: '4px',
                                      backgroundColor: ev.status === 'active' ? '#d1fae5' : '#f3f4f6',
                                      color: ev.status === 'active' ? '#065f46' : '#6b7280'
                                    }}>
                                      {ev.status === 'active' ? 'Active' : 'Passed'}
                                    </span>
                                  )}
                                  {isRemoved && (
                                    <span style={{
                                      marginLeft: '8px', fontSize: '12px', padding: '2px 6px', borderRadius: '4px',
                                      backgroundColor: '#fee2e2', color: '#991b1b'
                                    }}>
                                      Excluded
                                    </span>
                                  )}
                                </span>
                                <button
                                  type="button"
                                  style={{
                                    padding: '4px 12px',
                                    fontSize: '13px',
                                    background: isRemoved ? '#3b82f6' : '#ef4444',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                  }}
                                  onClick={async () => {
                                    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                                    if (isRemoved) {
                                      const res = await fetch('/api/content_plan/events', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf || '' },
                                        body: JSON.stringify({ event_id: ev.id })
                                      });
                                      const data = await res.json();
                                      if (res.ok && Array.isArray(data.events)) {
                                        setContentPlanEvents(data.events);
                                        toast.success('Event re-added to content plan');
                                      } else {
                                        toast.error(data.error || 'Failed to add event');
                                      }
                                    } else {
                                      const res = await fetch(`/api/content_plan/events/${ev.id}`, {
                                        method: 'DELETE',
                                        headers: { Accept: 'application/json', 'X-CSRF-Token': csrf || '' }
                                      });
                                      const data = await res.json();
                                      if (res.ok && Array.isArray(data.events)) {
                                        setContentPlanEvents(data.events);
                                        toast.success('Event excluded from content plan');
                                      } else {
                                        toast.error(data.error || 'Failed to remove event');
                                      }
                                    }
                                  }}
                                >
                                  {isRemoved ? 'Add' : 'Remove'}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
              {contentPlanSubTab === 'content-preferences' && (
                <div className="content-plan-preferences">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                    <div style={{ width: '100%' }}>
                      <label style={{ fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Generation Day</label>
                      <small className="text-muted" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Which day of the week should AI generate new post drafts?
                      </small>
                      <select
                        className="form-control"
                        style={{ width: '100%' }}
                        value={prefs.generation_day ?? 1}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPrefs((p) => ({ ...p, generation_day: parseInt(v, 10) }));
                        }}
                      >
                        <option value={1}>Monday</option>
                        <option value={2}>Tuesday</option>
                        <option value={3}>Wednesday</option>
                        <option value={4}>Thursday</option>
                        <option value={5}>Friday</option>
                        <option value={6}>Saturday</option>
                        <option value={7}>Sunday</option>
                      </select>
                    </div>
                    <div style={{ width: '100%' }}>
                      <label style={{ fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Post Scheduling Week</label>
                      <small className="text-muted" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Which week should the generated posts be scheduled for?
                      </small>
                      <select
                        className="form-control"
                        style={{ width: '100%' }}
                        value={prefs.generation_horizon ?? 1}
                        onChange={(e) => setPrefs((p) => ({ ...p, generation_horizon: parseInt(e.target.value, 10) }))}
                      >
                        <option value={1}>Schedule for next week</option>
                        <option value={2}>Schedule for 2 weeks out</option>
                        <option value={3}>Schedule for 3 weeks out</option>
                      </select>
                    </div>
                    <div style={{ width: '100%' }}>
                      <label style={{ fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Posts Per Day</label>
                      <small className="text-muted" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        How many posts should be generated per day for the scheduled week?
                      </small>
                      <input
                        type="number"
                        min={1}
                        max={5}
                        className="form-control"
                        style={{ width: '100%' }}
                        value={prefs.posts_per_day ?? 1}
                        onChange={(e) => setPrefs((p) => ({ ...p, posts_per_day: Math.min(5, Math.max(1, parseInt(e.target.value, 10) || 1)) }))}
                      />
                      <small className="text-muted" style={{ fontSize: '12px' }}>Max 5 per day</small>
                    </div>
                    <button
                      type="button"
                      className="btns btn-save-changes"
                      disabled={prefsSaving}
                      onClick={async () => {
                        setPrefsSaving(true);
                        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                        const res = await fetch('/api/content_plan', {
                          method: 'PATCH',
                          headers: {
                            'Content-Type': 'application/json',
                            Accept: 'application/json',
                            'X-CSRF-Token': csrf || ''
                          },
                          body: JSON.stringify({
                            content_plan: {
                              generation_day: prefs.generation_day,
                              generation_horizon: prefs.generation_horizon,
                              posts_per_day: prefs.posts_per_day
                            }
                          })
                        });
                        if (res.ok) {
                          const data = await res.json();
                          if (data.content_plan) setContentPlan(data.content_plan);
                          const dayNames = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                          const dayName = prefs.generation_day >= 1 && prefs.generation_day <= 7 ? dayNames[prefs.generation_day] : null;
                          toast.success(dayName
                            ? `Generation preferences saved. AI generation will run every ${dayName} at 11:00 AM UTC.`
                            : 'Generation preferences saved.');
                        } else {
                          const data = await res.json();
                          toast.error(data.errors ? data.errors[0] : 'Failed to save');
                        }
                        setPrefsSaving(false);
                      }}
                    >
                      {prefsSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  {contentPlan?.generation_day >= 1 && contentPlan?.generation_day <= 7 && (
                    <p className="text-muted" style={{ marginTop: '20px', fontSize: '14px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                      AI generation will run every {['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][contentPlan.generation_day]} at 11:00 AM UTC.
                    </p>
                  )}
                </div>
              )}
              {contentPlanSubTab === 'post-targeting' && (
                <div className="content-plan-post-targeting" style={{ paddingTop: '8px' }}>
                  <h4 style={{ marginBottom: 8, fontSize: '14px' }}>Post targeting</h4>
                  <p className="text-muted" style={{ marginBottom: 16, fontSize: '13px' }}>
                    The pages and accounts new posts go to by default, including posts from Agent Jobs and the Vesta Agent. Edit Post can change this for a single post.
                  </p>

                  <SocialAccountPicker
                    name="settings_target_accounts"
                    accounts={socialAccounts}
                    selected={defaultPick || defaultKeys}
                    onChange={setDefaultPick}
                    loading={socialAccountsLoading}
                    error={socialAccountsError}
                  />

                  <div style={{ marginTop: 16 }}>
                    <button
                      type="button"
                      className="btns btn-save-changes"
                      onClick={handleSavePostTargets}
                      disabled={savingDefaults || socialAccounts.length === 0 || (defaultPick || defaultKeys).length === 0}
                    >
                      {savingDefaults ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
              {contentPlanSubTab === 'generation-history' && (
                <div className="content-plan-report" style={{ paddingTop: '8px' }}>
                  {contentPlan?.content_plan_runs?.length > 0 ? (
                    <div style={{ fontSize: '14px' }}>
                      {contentPlan.content_plan_runs.map((run) => {
                        const posts = run.response_payload?.posts ?? run.response_payload?.created_posts ?? [];
                        const isTimedOut = run.status === 'triggered' && run.ran_at && (new Date() - new Date(run.ran_at)) > 5 * 60 * 1000;
                        const displayStatus = isTimedOut ? 'timed out' : run.status;
                        const statusColor = displayStatus === 'completed' ? '#166534' : displayStatus === 'failed' ? '#dc2626' : isTimedOut ? '#d97706' : '#6b7280';
                        const isExpanded = expandedReportRunIds.has(run.id);
                        const toggleRun = () => {
                          setExpandedReportRunIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(run.id)) next.delete(run.id);
                            else next.add(run.id);
                            return next;
                          });
                        };
                        return (
                          <div
                            key={run.id}
                            style={{
                              marginBottom: '12px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              backgroundColor: '#fafafa',
                              overflow: 'hidden'
                            }}
                          >
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={toggleRun}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRun(); } }}
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: '8px 16px',
                                padding: '12px 16px',
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                            >
                              <span style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', fontSize: '12px', color: '#6b7280' }}>▶</span>
                              <strong>
                                {run.ran_at ? new Date(run.ran_at).toLocaleString() : '—'}
                              </strong>
                              <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', backgroundColor: run.trigger_source === 'manual' ? '#dbeafe' : '#f3f4f6', color: '#374151' }}>
                                {run.trigger_source === 'manual' ? 'Manual' : 'Scheduled'}
                              </span>
                              <span style={{ fontSize: '12px', fontWeight: 500, color: statusColor, ...(isTimedOut ? { backgroundColor: '#fef3c7', padding: '2px 6px', borderRadius: '4px' } : {}) }}>
                                {displayStatus}
                              </span>
                              {run.error_message && (
                                <span className="text-muted" style={{ fontSize: '12px' }} title={run.error_message}>
                                  {run.error_message.slice(0, 80)}{run.error_message.length > 80 ? '…' : ''}
                                </span>
                              )}
                              {posts.length > 0 && !isExpanded && (
                                <span className="text-muted" style={{ fontSize: '12px', marginLeft: 'auto' }}>{posts.length} post(s)</span>
                              )}
                            </div>
                            {isExpanded && (
                              <div style={{ padding: '0 16px 12px 32px', borderTop: '1px solid #e5e7eb' }}>
                                {run.event_names?.length > 0 && (
                                  <p className="text-muted" style={{ fontSize: '12px', marginTop: '12px', marginBottom: '8px' }}>
                                    Events: {run.event_names.join(', ')}
                                  </p>
                                )}
                                {posts.length > 0 && (
                                  <div style={{ marginTop: '8px' }}>
                                    <strong style={{ fontSize: '13px' }}>{posts.length} post(s) created</strong>
                                    <ul style={{ marginTop: '8px', paddingLeft: '20px', listStyle: 'none' }}>
                                      {posts.map((p, i) => (
                                        <li key={i} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: i < posts.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                          <span className="text-muted" style={{ fontSize: '12px' }}>
                                            {(p.scheduled_date_utc || p.scheduled_at) && new Date(p.scheduled_date_utc || p.scheduled_at).toLocaleString()}
                                            {p.event_id && ` · Event ID: ${p.event_id}`}
                                          </span>
                                          <div style={{ marginTop: '4px' }}>{(p.content || p.message || '').slice(0, 200)}{(p.content || p.message || '').length > 200 ? '…' : ''}</div>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {run.status === 'triggered' && posts.length === 0 && (
                                  <p className="text-muted" style={{ fontSize: '13px', marginTop: '8px' }}>
                                    {isTimedOut
                                      ? `This generation appears to have timed out (started ${Math.round((new Date() - new Date(run.ran_at)) / 60000)} minutes ago). Try generating again from the Generate Now tab.`
                                      : 'Run in progress or response not yet received.'}
                                  </p>
                                )}
                                {run.status === 'failed' && !run.error_message && (
                                  <p className="text-muted" style={{ fontSize: '13px', marginTop: '8px' }}>No report data for this run.</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : contentPlan?.last_run_at || contentPlan?.last_run_response ? (
                    (() => {
                      const data = contentPlan.last_run_response;
                      const items = Array.isArray(data) ? data : (data ? [data] : []);
                      if (items.length === 0) {
                        return (
                          <p className="text-muted">
                            {contentPlan.last_run_at ? `Last run at ${new Date(contentPlan.last_run_at).toLocaleString()}. No report data.` : 'No report data.'}
                          </p>
                        );
                      }
                      return (
                        <div style={{ fontSize: '14px' }}>
                          {contentPlan.last_run_at && (
                            <p className="text-muted" style={{ marginBottom: '12px', fontSize: '14px' }}>
                              Last run at {new Date(contentPlan.last_run_at).toLocaleString()}.
                            </p>
                          )}
                          {items.map((item, idx) => {
                            const posts = item.posts ?? item.created_posts ?? [];
                            return (
                              <div key={idx} style={{ marginBottom: '20px', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', backgroundColor: '#fafafa' }}>
                                <strong>Company ID: {item.company_ext_id ?? item.company_id ?? '—'}</strong>
                                <span style={{ marginLeft: '8px', color: '#6b7280' }}>{posts.length} post(s) created</span>
                                {posts.length > 0 && (
                                  <ul style={{ marginTop: '12px', paddingLeft: '20px', listStyle: 'none' }}>
                                    {posts.map((p, i) => (
                                      <li key={i} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: i < posts.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                        <span className="text-muted" style={{ fontSize: '12px' }}>
                                          {(p.scheduled_date_utc || p.scheduled_at) && new Date(p.scheduled_date_utc || p.scheduled_at).toLocaleString()}
                                          {p.event_id && ` · Event ID: ${p.event_id}`}
                                        </span>
                                        <div style={{ marginTop: '4px' }}>{(p.content || p.message || '').slice(0, 200)}{(p.content || p.message || '').length > 200 ? '…' : ''}</div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  ) : (
                    <p className="text-muted">No report yet. Reports appear here after AI generation runs.</p>
                  )}
                </div>
              )}
              {contentPlanSubTab === 'generate-now' && (
                <div style={{ paddingTop: '8px' }}>
                  <h4 style={{ marginBottom: '8px', fontSize: '14px' }}>Generate Posts Now</h4>
                  <p className="text-muted" style={{ marginBottom: '16px', fontSize: '13px' }}>
                    Run AI content generation now using your current content plan settings.
                    This will generate draft social media posts for your upcoming events based on
                    your content preferences, event selection, and post targeting configuration.
                  </p>
                  <button
                    type="button"
                    className="btns btn-save-changes"
                    disabled={!contentPlan?.id || contentPlanLoading || triggeringGeneration}
                    onClick={handleTriggerGeneration}
                  >
                    {triggeringGeneration ? 'Generating...' : 'Generate Posts Now'}
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'accounts' && (
            <div className="connect-page-content ap-tab">
              <MetaAuthHelperDisclosure />

              {connectionsError && (
                <div className="ap-errorbar" role="alert">
                  <span>{connectionsError}</span>
                  <button type="button" className="ap-textbtn" onClick={() => loadConnections()}>Try again</button>
                </div>
              )}

              {removeError && (
                <div className="ap-errorbar" role="alert">
                  <span>{removeError}</span>
                  <button
                    type="button"
                    className="ap-errorbar-dismiss"
                    aria-label="Dismiss"
                    onClick={() => setRemoveError(null)}
                  >
                    ✕
                  </button>
                </div>
              )}

              {connectionsLoading && !facebookStatus ? (
                <div className="ap-loading">Loading your connections…</div>
              ) : (
                <React.Fragment>
                  {/* ── Section 1: the logins ── */}
                  <div className="ap-section">
                    <div className="ap-section-head">
                      <div>
                        <h3 className="ap-section-title">Connected Accounts</h3>
                        <p className="ap-section-sub">The logins Vesta uses to reach your Pages and profiles.</p>
                      </div>
                      <div className="ap-section-actions">
                        {accountRows.length > 0 && (
                          <button
                            type="button"
                            className="ap-textbtn"
                            onClick={() => setSelectedKeys((prev) => toggleSection(prev, accountRows))}
                          >
                            {sectionSelectAllLabel(selectedKeys, accountRows)}
                          </button>
                        )}
                        <button type="button" className="ap-btn ap-btn--outline" onClick={() => setChoiceMode('account')}>
                          <span aria-hidden="true">+</span> Connect Account
                        </button>
                      </div>
                    </div>

                    {accountRows.length === 0 ? (
                      <ConnectionsEmptyState
                        title="No accounts connected"
                        body="Connect Facebook or Instagram to choose Pages for Vesta to post to."
                        ctaLabel="Connect Account"
                        onCta={() => setChoiceMode('account')}
                      />
                    ) : (
                      <div className="ap-list">
                        {accountRows.map((row) => (
                          <ConnectionRow
                            key={row.key}
                            row={row}
                            selected={selectedKeys.has(row.key)}
                            onSelect={handleSelect}
                            onRemove={handleRowRemove}
                            onToggleActive={handleTogglePage}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Section 2: where posts actually go ── */}
                  <div className="ap-section">
                    <div className="ap-section-head">
                      <div>
                        <h3 className="ap-section-title">Connected Pages &amp; Profiles</h3>
                        <p className="ap-section-sub">Vesta posts to every Active Page and profile. Untick one to stop posting there without disconnecting it.</p>
                      </div>
                      <div className="ap-section-actions">
                        {pageRows.length > 0 && (
                          <button
                            type="button"
                            className="ap-textbtn"
                            onClick={() => setSelectedKeys((prev) => toggleSection(prev, pageRows))}
                          >
                            {sectionSelectAllLabel(selectedKeys, pageRows)}
                          </button>
                        )}
                        <button type="button" className="ap-btn ap-btn--outline" onClick={() => setChoiceMode('page')}>
                          <span aria-hidden="true">+</span> Add Another Page
                        </button>
                      </div>
                    </div>

                    {pageRows.length === 0 ? (
                      <ConnectionsEmptyState
                        title="No Pages authorized yet"
                        body="Vesta has nowhere to post. Authorize at least one Page or profile."
                        ctaLabel="Add Another Page"
                        onCta={() => setChoiceMode('page')}
                      />
                    ) : (
                      <div className="ap-list">
                        {pageRows.map((row) => (
                          <ConnectionRow
                            key={row.key}
                            row={row}
                            selected={selectedKeys.has(row.key)}
                            onSelect={handleSelect}
                            onRemove={handleRowRemove}
                            onToggleActive={handleTogglePage}
                            onReauthenticate={openInstagramStandalonePopup}
                            toggling={togglingPageId === row.id}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Bulk action bar — sticks to the bottom of the modal's own scroll area. */}
                  {selectedCount > 0 && (
                    <div className="ap-bar">
                      <span className="ap-bar-count">{selectionLabel(selectedCount)}</span>
                      <button
                        type="button"
                        className="ap-btn ap-btn--ondark"
                        onClick={() => setSelectedKeys(new Set())}
                      >
                        Clear
                      </button>
                      <button type="button" className="ap-btn ap-btn--danger" onClick={handleBulkRemove}>
                        Remove selected
                      </button>
                    </div>
                  )}
                </React.Fragment>
              )}

              {choiceMode && (
                <PlatformChooser mode={choiceMode} onPick={handleChoice} onCancel={() => setChoiceMode(null)} />
              )}

              {confirmTarget && (
                <ConfirmRemoveDialog
                  items={confirmTarget.items}
                  cascadedPageCount={confirmTarget.cascadedPageCount}
                  busy={removing}
                  onCancel={() => setConfirmTarget(null)}
                  onConfirm={performRemoval}
                />
              )}
            </div>
          )}

          {activeTab === 'instructions' && (
            <div className="connect-page-content">
              <h3 className="instructions-title">Vesta AI Special Organic Social Media Instructions</h3>
              <textarea
                className="form-control description"
                rows="6"
                placeholder="Add any instructions for Vesta AI to follow when drafting organic posts..."
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
              />
            </div>
          )}

          {activeTab === 'media-assets' && (
            <div className="connect-page-content">
              <div>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px', display: 'block' }}>
                  Media Assets URL
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://example.com/media-assets"
                    value={mediaAssetsUrl}
                    onChange={(e) => setMediaAssetsUrl(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {mediaAssetsUrl && (
                    <button
                      type="button"
                      className="btns btn-primarys"
                      onClick={() => {
                        const url = mediaAssetsUrl.trim();
                        const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
                        window.open(href, '_blank', 'noopener,noreferrer');
                      }}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Open Link
                    </button>
                  )}
                </div>
                <small className="text-muted" style={{ display: 'block', marginTop: '8px' }}>
                  Enter the URL of media assets folder or collection
                </small>
              </div>
            </div>
          )}

        </div>

        <div className="modal-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <span>
            {(activeTab === 'instructions' || activeTab === 'media-assets') && (
              <button type="button" className="btns btn-save-changes" onClick={handleSave}>Save</button>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

// connect a page modal
const ConnectPageModal = ({ isOpen, onClose, onFacebookConnect }) => {
  // Get Facebook connection status from DOM
  const getFacebookStatusFromDOM = () => {
    const reactRoot = document.getElementById('react-root');
    const facebookConnected = reactRoot?.getAttribute('facebook-connected') === 'true';
    let pagesLen = 0;
    try {
      const pagesJson = reactRoot?.getAttribute('facebook-pages') || '[]';
      const pages = JSON.parse(pagesJson);
      pagesLen = Array.isArray(pages) ? pages.length : 0;
    } catch (_e) { pagesLen = 0; }
    return { connected: facebookConnected && pagesLen > 0 };
  };

  // These start from the server-rendered DOM attributes but must be able to change WITHOUT a
  // navigation: the OAuth popups no longer reload the page, so the gate has to notice a new
  // connection on its own. The attributes are written back after each refresh so the rest of the
  // page (which still reads them) agrees with what this modal is showing.
  const [facebookData, setFacebookData] = useState(getFacebookStatusFromDOM);
  const [instagramConnectedAttr, setInstagramConnectedAttr] = useState(
    () => document.getElementById('react-root')?.getAttribute('instagram-connected') === 'true'
  );
  const [instagramStandaloneConnected, setInstagramStandaloneConnected] = useState(false);

  const refreshConnections = useCallback(async () => {
    try {
      const [fbRes, igRes] = await Promise.all([
        fetch('/api/facebook/connection_status', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
        fetch('/api/instagram/connection_status', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
      ]);
      const [fb, ig] = await Promise.all([
        fbRes.json().catch(() => null),
        igRes.json().catch(() => null),
      ]);
      if (fb) {
        const fbPages = Array.isArray(fb.pages) ? fb.pages : [];
        setFacebookData({ connected: Boolean(fb.connected) && fbPages.length > 0 });
      }
      if (ig) {
        const igAccounts = Array.isArray(ig.accounts) ? ig.accounts : [];
        setInstagramConnectedAttr(Boolean(ig.connected));
        setInstagramStandaloneConnected(igAccounts.some((acc) => acc.stand_alone === true));
      }
      mirrorConnectionAttrs(fb, ig);
    } catch (_e) { /* keep showing what we had rather than blanking the gate */ }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    refreshConnections();
  }, [isOpen, refreshConnections]);

  const openOAuthPopup = useOAuthPopup(refreshConnections);

  const openFacebookPopup = () => openOAuthPopup('/facebook/oauth/authorize_social_media?popup=1', 'fbAuth');
  const openInstagramPopup = () => openOAuthPopup('/facebook/oauth/authorize_instagram?popup=1', 'igAuth');
  const openInstagramStandalonePopup = () => openOAuthPopup('/facebook/oauth/authorize_instagram_standalone?popup=1', 'igStandaloneAuth');

  if (!isOpen) return null;

  return (
    <div className="modal-overlay connect-page-modal" onClick={(e) => {
      // Prevent closing modal by clicking outside when no accounts are connected
      const facebookConnected = facebookData.connected;
      const hasAnyConnection = facebookConnected || instagramConnectedAttr || instagramStandaloneConnected;
      if (!hasAnyConnection) {
        e.stopPropagation();
      }
    }}>
      <div className="modal-container connect-page-container" onClick={(e) => e.stopPropagation()}>
        <div className="connect-page-header">
          <h2>Connect Facebook or instagram</h2>
          {/* Remove close button - modal should always be visible */}
        </div>

        <div className="connect-page-content">
          <MetaAuthHelperDisclosure />
          <div className="platform-tiles">
            {/* Facebook Tile */}
            <div
              className={`platform-tile facebook-tile ${facebookData.connected ? 'connected' : ''}`}
              onClick={facebookData.connected ? null : openFacebookPopup}
            >
              <div className="platform-icon">
                {facebookData.connected ? (
                  <div className="connected-overlay">
                    <div className="facebook-icon-blurred">
                      <img
                        src="/assets/icons/facebook-icon.png"
                        alt="Facebook Icon"
                        width="40"
                        height="40"
                        style={{ filter: 'blur(2px)', opacity: 0.6 }}
                      />
                    </div>
                    <div className="connected-text">Already Connected!</div>
                  </div>
                ) : (
                  <img
                    src="/assets/icons/facebook-icon.png"
                    alt="Facebook Icon"
                    width="40"
                    height="40"
                  />
                )}
              </div>
              <h3 style={facebookData.connected ? { filter: 'blur(2px)', opacity: 0.6 } : {}}>Connect Facebook</h3>
              <p style={facebookData.connected ? { filter: 'blur(2px)', opacity: 0.6 } : {}}>Page or Group</p>
            </div>

            {/* Instagram Tile */}
            <div
              className={`platform-tile instagram-tile ${instagramConnectedAttr ? 'connected' : ''}`}
              onClick={instagramConnectedAttr ? null : openInstagramPopup} >
              <div className="platform-icon">
                {instagramConnectedAttr ? (
                  <div className="connected-overlay">
                    <div className="facebook-icon-blurred">
                      <img
                        src="/assets/icons/insta-icon.png"
                        alt="Instagram Icon"
                        width="40"
                        height="40"
                        style={{ filter: 'blur(2px)', opacity: 0.6 }}
                      />
                    </div>
                    <div className="connected-text">Already Connected!</div>
                  </div>
                ) : (
                  <img
                    src="/assets/icons/insta-icon.png"
                    alt="Instagram Icon"
                    width="40"
                    height="40"
                  />
                )}
              </div>
              <h3 style={instagramConnectedAttr ? { filter: 'blur(2px)', opacity: 0.6 } : {}}>Connect Instagram</h3>
              <p style={instagramConnectedAttr ? { filter: 'blur(2px)', opacity: 0.6 } : {}}>Via Facebook Page</p>
            </div>

            {/* Instagram Standalone Tile */}
            <div
              className={`platform-tile instagram-tile instagram-standalone-tile ${instagramStandaloneConnected ? 'connected' : ''}`}
              onClick={instagramStandaloneConnected ? null : openInstagramStandalonePopup} >
              <div className="platform-icon">
                {instagramStandaloneConnected ? (
                  <div className="connected-overlay">
                    <div className="facebook-icon-blurred">
                      <img
                        src="/assets/icons/insta-icon.png"
                        alt="Instagram Standalone Icon"
                        width="40"
                        height="40"
                        style={{ filter: 'blur(2px)', opacity: 0.6 }}
                      />
                    </div>
                    <div className="connected-text">Already Connected!</div>
                  </div>
                ) : (
                  <img
                    src="/assets/icons/insta-icon.png"
                    alt="Instagram Standalone Icon"
                    width="40"
                    height="40"
                  />
                )}
              </div>
              <h3 style={instagramStandaloneConnected ? { filter: 'blur(2px)', opacity: 0.6 } : {}}>Connect Instagram</h3>
              <p style={instagramStandaloneConnected ? { filter: 'blur(2px)', opacity: 0.6 } : {}}>Direct (Business or Creator)</p>
            </div>
          </div>

          {(facebookData.connected || instagramConnectedAttr || instagramStandaloneConnected) && (
            <div className="proceed-link">
              <a href="#" onClick={(e) => { e.preventDefault(); onClose(); }}>
                I want to proceed with one channel for now &gt;
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SocialMediaAIPage = () => {
  const rootElement = document.getElementById('react-root');
  const [currentWeek, setCurrentWeek] = useState(new Date())
  const [postsData, setPostsData] = useState({})
  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [showDebugTools, setShowDebugTools] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [promotedEvents, setPromotedEvents] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (rootElement) {
      const id = rootElement.getAttribute('company-id');
      setCompanyId(id);
      // Open connect modal only if there are no connected accounts
      const facebookConnected = rootElement.getAttribute('facebook-connected') === 'true';
      const instagramConnected = rootElement.getAttribute('instagram-connected') === 'true';
      let pagesLen = 0;
      try {
        const pagesJson = rootElement.getAttribute('facebook-pages') || '[]';
        const pages = JSON.parse(pagesJson);
        pagesLen = Array.isArray(pages) ? pages.length : 0;
      } catch (_e) { pagesLen = 0; }

      const fbReady = facebookConnected && pagesLen > 0;
      const hasConnectedAccounts = fbReady || instagramConnected;
      setConnectModalOpen(!hasConnectedAccounts);

      // Check if generation is in progress
      const generating = sessionStorage.getItem('socialMediaAI_generating') === 'true';
      if (generating) {
        setIsGenerating(true);
        setProgressMessage('Initializing...');
      }
    }
  }, []);

  // Fetch promoted events
  useEffect(() => {
    fetch("/api/marketing/all_upcoming_events")
      .then(res => res.json())
      .then(data => {
        if (data && data.events) {
          // passed events don't show up in Create Custom Post.
          const now = new Date();
          const filtered = data.events.filter((e) => {
            const end = e?.end_date || e?.endDate || e?.endDateUTC || e?.endDateUtc;
            if (!end) return true;
            const d = new Date(end);
            return Number.isNaN(d.getTime()) ? true : d >= now;
          });
          setPromotedEvents(filtered);
        }
      })
      .catch(error => console.error("Error fetching promoted events:", error));
  }, []);

  // Time-based progress message updates
  useEffect(() => {
    if (!isGenerating) return;

    const startTime = sessionStorage.getItem('socialMediaAI_generation_start');
    if (!startTime) return;

    const updateMessage = () => {
      const elapsed = Date.now() - parseInt(startTime);
      const elapsedSeconds = elapsed / 1000;
      const elapsedMinutes = elapsedSeconds / 60;

      let message = 'Initializing...';

      // Time-based message progression:
      // 0-1 minute: Initializing
      // 1-2.15 minutes: AI generating posts
      // 2.15-3.5 minutes: Finalizing
      // Above 3.5 minutes: Almost done

      if (elapsedMinutes < 1) {
        message = 'Initializing...';
      } else if (elapsedMinutes < 2.15) {
        message = 'AI generating posts...';
      } else if (elapsedMinutes < 3.5) {
        message = 'Finalizing...';
      } else {
        message = 'Almost done...';
      }

      setProgressMessage(message);
    };

    // Update immediately
    updateMessage();

    // Update every 5 seconds to show appropriate message based on elapsed time
    // This ensures smooth transitions and accurate messaging
    const interval = setInterval(updateMessage, 5000);

    // Auto-hide progress after 5 minutes (safety timeout)
    const maxTime = 5 * 60 * 1000;
    const timeout = setTimeout(() => {
      setIsGenerating(false);
      sessionStorage.removeItem('socialMediaAI_generating');
      sessionStorage.removeItem('socialMediaAI_generation_start');
    }, maxTime);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isGenerating]);

  const alertConfigurations = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
  };

  useEffect(() => {
    refreshCompanyPostsData();
  }, [])

  const refreshCompanyPostsData = () => {
    return fetch("/company_posts")
      .then(res => res.json())
      .then(data => {
        // Process the data to group by appropriate date based on status
        const processedData = {};

        // Iterate through all dates in the data
        Object.keys(data).forEach(dateKey => {
          const posts = data[dateKey];

          // Process each post
          posts.forEach(post => {
            // Determine which date to use for grouping based on status and available dates
            let groupDate;
            const tz = (post?.event && post.event.timezone) || post?.timezone || 'UTC';

            if (post.status === 'published' && post.published_at) {
              // Published posts are grouped by published_at
              groupDate = new Date(post.published_at);
            } else if (post.scheduled_at) {
              // Any post with scheduled_at should stay on its scheduled day (even if failed later)
              groupDate = new Date(post.scheduled_at);
            } else if (post.created_at) {
              // Otherwise group by created_at
              groupDate = new Date(post.created_at);
            } else {
              // If no dates are available, use the current date key
              groupDate = new Date(dateKey);
            }

            // Format the date for grouping in the post's event timezone
            const formattedDate = formatDateInTz(groupDate, tz);
            // Attach display time in event timezone for card rendering
            const displayBase = post.published_at || post.scheduled_at || post.created_at || groupDate;
            post.time = formatTimeInTz(displayBase, tz);

            // Initialize the array for this date if it doesn't exist
            if (!processedData[formattedDate]) {
              processedData[formattedDate] = [];
            }

            // Add the post to the appropriate date group
            processedData[formattedDate].push(post);
          });
        });

        setPostsData(processedData);

        // If the Edit Post modal is open, keep its `post` prop in sync
        // so the radio selection reflects the updated company targets.
        if (currentPost?.id != null) {
          const updatedCurrent = Object.values(processedData)
            .flat()
            .find(p => p && String(p.id) === String(currentPost.id));
          if (updatedCurrent) setCurrentPost(updatedCurrent);
        }

        if (data && Object.keys(data).length > 0) {
          const firstDate = Object.keys(data)[0];
          if (data[firstDate] && data[firstDate].length > 0 && data[firstDate][0].company_id) {
            setCompanyId(data[firstDate][0].company_id);
          }
        }
      })
      .catch(error => console.error("Error fetching posts:", error));
  };

  useEffect(() => {

    if (!consumer) {
      return;
    }

    if (!companyId) {
      return;
    }

    const subscription = consumer.subscriptions.create(
      {
        channel: "CompanyPostsChannel",
        company_id: companyId
      },
      {
        initialized() {
        },
        connected() {
          try {
            this.perform('test_connection');
          } catch (err) {
            console.error("[ActionCable] Error performing test_connection:", err);
          }
        },
        disconnected() {
          console.warn("[ActionCable] Disconnected from CompanyPostsChannel");
        },
        rejected() {
          console.error("[ActionCable] Subscription rejected - check Rails logs (server-side)");
        },
        received(data) {
          try {
            if (data?.action === "posts_created") {
              toast.success(data.message, alertConfigurations);
              setTimeout(() => setNotification(null), 5000);
              // Hide progress header when posts are created
              setIsGenerating(false);
              sessionStorage.removeItem('socialMediaAI_generating');
              sessionStorage.removeItem('socialMediaAI_generation_start');
            }

            if (data?.action === "error") {
              toast.error(data.message, alertConfigurations);
              setTimeout(() => setNotification(null), 5000);
              // Hide progress header on error
              setIsGenerating(false);
              sessionStorage.removeItem('socialMediaAI_generating');
              sessionStorage.removeItem('socialMediaAI_generation_start');
            }

            if (data?.action === "posts_data_created") {
              handleNewPostsData(data.posts);
              // Hide progress header when posts data is received
              setIsGenerating(false);
              sessionStorage.removeItem('socialMediaAI_generating');
              sessionStorage.removeItem('socialMediaAI_generation_start');
            }
          } catch (err) {
            console.error("[ActionCable] Error handling received data:", err);
          }
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, [companyId]);

  const handleDelete = async (postId) => {
    if (!confirm("Are you sure you want to delete this post?")) return;

    try {
      const response = await fetch(`/company_posts/${postId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        setPostsData(prevPostsData => {
          const updatedPostsData = { ...prevPostsData };

          // Find and remove the post from all date groups
          Object.keys(updatedPostsData).forEach(dateKey => {
            if (Array.isArray(updatedPostsData[dateKey])) {
              updatedPostsData[dateKey] = updatedPostsData[dateKey].filter(
                post => post.id !== postId
              );

              // Remove empty date arrays to keep state clean
              if (updatedPostsData[dateKey].length === 0) {
                delete updatedPostsData[dateKey];
              }
            }
          });

          return updatedPostsData;
        });

        toast.success("Post deleted successfully.", alertConfigurations);
      } else {
        toast.error("Failed to delete post.", alertConfigurations);
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Something went wrong.", alertConfigurations);
    }
  };

  const openEditModal = (post) => {
    setCurrentPost(post);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentPost(null);
  };

  const handleCreatePost = async (postData) => {
    try {
      const payload = {
        event_id: postData.event_id,
        message: postData.description,
        image_url: postData.media_url || null,
        scheduled_at: postData.scheduled_at || null,
        media_type: postData.media_type || null,
        channel_media: postData.channel_media || {},
        ...(postData.target_accounts ? { target_accounts: postData.target_accounts } : {})
      };

      const response = await fetch("/company_posts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Post created successfully!", alertConfigurations);
        setCreateModalOpen(false);
        // Refresh posts data without full page reload
        fetch("/company_posts")
          .then(res => res.json())
          .then(postsData => {
            // Process the data to group by appropriate date based on status (same logic as initial load)
            const processedData = {};

            Object.keys(postsData).forEach(dateKey => {
              const posts = postsData[dateKey];

              posts.forEach(post => {
                let groupDate;
                const tz = (post?.event && post.event.timezone) || post?.timezone || 'UTC';

                if (post.status === 'published' && post.published_at) {
                  groupDate = new Date(post.published_at);
                } else if (post.scheduled_at) {
                  groupDate = new Date(post.scheduled_at);
                } else if (post.created_at) {
                  groupDate = new Date(post.created_at);
                } else {
                  groupDate = new Date(dateKey);
                }

                const formattedDate = formatDateInTz(groupDate, tz);
                const displayBase = post.published_at || post.scheduled_at || post.created_at || groupDate;
                post.time = formatTimeInTz(displayBase, tz);

                if (!processedData[formattedDate]) {
                  processedData[formattedDate] = [];
                }
                processedData[formattedDate].push(post);
              });
            });

            setPostsData(processedData);
          })
          .catch(error => {
            console.error("Error refreshing posts:", error);
            // Fallback to page reload if fetch fails
            window.location.reload();
          });
      } else {
        toast.error(data.error || "Failed to create post", alertConfigurations);
      }
    } catch (error) {
      console.error("Error creating post:", error);
      toast.error("Something went wrong. Please try again.", alertConfigurations);
    }
  };

  const handleNewPostsData = (newPosts) => {
    if (!newPosts || newPosts.length === 0) return;

    // Use functional state update to ensure we're working with the latest state
    setPostsData(prevPostsData => {
      // Create a completely new object structure
      const currentPosts = prevPostsData || {};

      // Process each new post and create new date groups
      const newPostsByDate = {};

      newPosts.forEach(post => {
        // Check if this post already exists in current state
        const postExists = Object.values(currentPosts).some(datePosts =>
          Array.isArray(datePosts) && datePosts.some(existingPost =>
            existingPost && existingPost.id === post.id
          )
        );

        if (postExists) {
          return;
        }

        // Determine which date to use for grouping
        let groupDate;
        const tz = (post?.event && post.event.timezone) || post?.timezone || 'UTC';

        if (post.status === 'published' && post.published_at) {
          groupDate = new Date(post.published_at);
        } else if (post.status === 'draft' && post.scheduled_at) {
          groupDate = new Date(post.scheduled_at);
        } else if (post.status === 'scheduled' && post.scheduled_at) {
          groupDate = new Date(post.scheduled_at);
        } else if (post.created_at) {
          groupDate = new Date(post.created_at);
        } else {
          groupDate = new Date();
        }

        const formattedDate = formatDateInTz(groupDate, tz);
        const displayBase = post.published_at || post.scheduled_at || post.created_at || groupDate;
        post.time = formatTimeInTz(displayBase, tz);

        // Add to new posts by date
        if (!newPostsByDate[formattedDate]) {
          newPostsByDate[formattedDate] = [];
        }
        newPostsByDate[formattedDate].push(post);
      });

      // Merge existing posts with new posts
      const mergedPosts = { ...currentPosts };

      Object.keys(newPostsByDate).forEach(date => {
        if (mergedPosts[date]) {
          // If date exists, add new posts to existing array
          mergedPosts[date] = [...mergedPosts[date], ...newPostsByDate[date]];
        } else {
          // If date doesn't exist, create new array
          mergedPosts[date] = [...newPostsByDate[date]];
        }
      });

      return mergedPosts;
    });
  };

  const savePostChanges = (updatedPost, action = "update") => {
    // Check if Facebook pages are connected before updating
    const getFacebookPagesFromDOM = () => {
      const reactRoot = document.getElementById('react-root');
      const facebookPagesJson = reactRoot?.getAttribute('facebook-pages') || '[]';
      try {
        return JSON.parse(facebookPagesJson);
      } catch (error) {
        console.error('Error parsing Facebook pages data:', error);
        return [];
      }
    };

    const getInstagramConnectionStatus = () => {
      const reactRoot = document.getElementById('react-root');
      return reactRoot?.getAttribute('instagram-connected') === 'true';
    };

    const facebookPages = getFacebookPagesFromDOM();
    const instagramConnected = getInstagramConnectionStatus();
    if (action === 'schedule' && (!facebookPages || facebookPages.length === 0) && !instagramConnected) {
      toast.error(
        'You need to add a Facebook page or instagram account before approving posts.',
        alertConfigurations
      );
      return;
    }

    // Process the datetime-local value
    let updatedPostWithTime = { ...updatedPost };

    // Determine which date to use for grouping based on status and available dates
    let groupDate;
    let shouldRegroup = false;

    if (updatedPost.scheduledDateTime) {
      // Extract time in the format needed for display (e.g., "8:00 am")
      const tz = (updatedPost?.event && updatedPost.event.timezone) || updatedPost?.timezone || 'UTC';
      // scheduledDateTime is in event timezone; convert to UTC ISO for server using moment
      const localMoment = moment.tz(updatedPost.scheduledDateTime, 'YYYY-MM-DDTHH:mm', tz);
      const utcInstant = localMoment.clone().utc();
      updatedPostWithTime.time = localMoment.format('h:mm a');
      updatedPostWithTime.date = utcInstant.toISOString();
      updatedPostWithTime.scheduled_at = utcInstant.toISOString();

      // Use the scheduled date for grouping if post is draft or scheduled
      if (updatedPost.status === 'draft' || updatedPost.status === 'scheduled') {
        groupDate = localMoment.toDate();
        shouldRegroup = true;
      }
    }

    // Handle grouping based on status
    if (updatedPost.status === 'published') {
      // If post is published, use published_at for grouping
      if (updatedPost.published_at) {
        groupDate = new Date(updatedPost.published_at);
      } else {
        // If no published_at exists, set it to current time
        const now = new Date();
        updatedPostWithTime.published_at = now.toISOString();
        groupDate = now;
      }
      shouldRegroup = true;
    } else if (updatedPost.status === 'rejected' || updatedPost.status === 'draft') {
      // If post is rejected or draft without scheduled_at, use created_at
      if (!updatedPostWithTime.scheduled_at && updatedPost.created_at) {
        groupDate = new Date(updatedPost.created_at);
        shouldRegroup = true;
      }
    }

    // Prepare data for API call
    const postData = {
      id: updatedPost.id,
      message: updatedPostWithTime.description, // Map description to message
      status: updatedPostWithTime.status,
      social_media_post_targets: updatedPostWithTime.social_media_post_targets
    };

    // Include media fields - send empty string if media was removed, otherwise send the URL
    if (updatedPost.hasOwnProperty('image') || updatedPost.hasOwnProperty('media_url')) {
      postData.image_url = updatedPost.image || updatedPost.media_url || '';
    }
    if (updatedPost.hasOwnProperty('media_type')) {
      postData.media_type = updatedPost.media_type || '';
    }
    if (updatedPost.hasOwnProperty('channel_media')) {
      postData.channel_media = updatedPost.channel_media || {};
    }
    // Only an actual change in "Post on" is sent; otherwise the post keeps following its pick or
    // the company default (and a since-disconnected account can't block an unrelated edit).
    if (updatedPost.target_accounts_touched) {
      postData.target_accounts = updatedPost.target_accounts || [];
    }

    // Only include scheduled_at if it's present and we're not publishing
    if (updatedPostWithTime.date && updatedPostWithTime.status !== 'published') {
      postData.scheduled_at = updatedPostWithTime.date;

      // Client-side validation: approving, or re-saving an already approved post, needs a
      // future time (the publish job is re-enqueued for it). A draft may keep a past time.
      if (requiresFutureTime(updatedPost, action)) {
        const scheduledTime = new Date(updatedPostWithTime.date);
        const now = new Date();

        if (scheduledTime <= now) {
          toast.error('Scheduled time must be in the future. Please select a future date and time.', alertConfigurations);
          return; // Don't proceed with the API call
        }
      }
    }

    const endpoint = action === "schedule"
      ? `/company_posts/${updatedPost.id}/schedule_and_approve`
      : `/company_posts/${updatedPost.id}`;

    const method = action === "schedule" ? "POST" : "PATCH";

    fetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify(postData)
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Only update state after successful API response
        setPostsData(prevPostsData => {
          const currentPosts = prevPostsData || {};
          const updatedPostsData = { ...currentPosts };

          // Find the current date key that contains this post
          let currentDateKey = null;
          Object.keys(updatedPostsData).forEach(dateKey => {
            if (updatedPostsData[dateKey].some(p => p.id === updatedPost.id)) {
              currentDateKey = dateKey;
            }
          });

          // If we need to regroup the post
          if (shouldRegroup && groupDate && currentDateKey) {
            const newDateKey = formatDate(groupDate);

            // If the post needs to move to a different date
            if (currentDateKey !== newDateKey) {
              // Remove from current date
              updatedPostsData[currentDateKey] = updatedPostsData[currentDateKey].filter(
                p => p.id !== updatedPost.id
              );

              // Add to new date
              if (!updatedPostsData[newDateKey]) {
                updatedPostsData[newDateKey] = [];
              }
              updatedPostsData[newDateKey].push({
                ...updatedPostWithTime,
                image: updatedPost.image || updatedPost.media_url || null,
                media_url: updatedPost.image || updatedPost.media_url || null,
                media_type: updatedPost.media_type || null
              });
            } else {
              // Just update in place
              const postIndex = updatedPostsData[currentDateKey].findIndex(p => p.id === updatedPost.id);
              if (postIndex !== -1) {
                // Update with media fields
                updatedPostsData[currentDateKey][postIndex] = {
                  ...updatedPostWithTime,
                  image: updatedPost.image || updatedPost.media_url || null,
                  media_url: updatedPost.image || updatedPost.media_url || null,
                  media_type: updatedPost.media_type || null
                };
              }
            }
          } else {
            // If no regrouping needed, just update the post in its current location
            if (currentDateKey) {
              const postIndex = updatedPostsData[currentDateKey].findIndex(p => p.id === updatedPost.id);
              if (postIndex !== -1) {
                // Update with media fields
                updatedPostsData[currentDateKey][postIndex] = {
                  ...updatedPostWithTime,
                  image: updatedPost.image || updatedPost.media_url || null,
                  media_url: updatedPost.image || updatedPost.media_url || null,
                  media_type: updatedPost.media_type || null
                };
              }
            }
          }

          return updatedPostsData;
        });

        closeModal();
        toast.success('Post updated successfully!', alertConfigurations);
      } else {
        // Display error message from controller
        toast.error(data.error || 'Failed to update post', alertConfigurations);
      }
    })
    .catch(error => {
      console.error('Error updating post:', error);
      toast.error('An error occurred while updating the post', alertConfigurations);
    });
  };

  const getWeekDates = (date) => {
    const startOfWeek = new Date(date)
    startOfWeek.setDate(date.getDate() - date.getDay() + 1) // Start from Monday

    const dates = []
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startOfWeek)
      currentDate.setDate(startOfWeek.getDate() + i)
      dates.push(currentDate)
    }
    return dates
  }

  const formatDate = (date) => {
    return date.toISOString().split('T')[0]
  }

  const formatDisplayDate = (date) => {
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
    const dayName = days[date.getDay()]
    const dayNumber = date.getDate().toString().padStart(2, '0')
    return `${dayName} - ${dayNumber}`
  }

  const getWeekRange = (date) => {
    const dates = getWeekDates(date)
    const startDate = dates[0]
    const endDate = dates[6]

    const startMonth = startDate.toLocaleString('default', { month: 'long' })
    const startDay = startDate.getDate()
    const endMonth = endDate.toLocaleString('default', { month: 'short' })
    const endDay = endDate.getDate()

    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startDate.getFullYear()}`
  }

  const weekDates = getWeekDates(currentWeek)

  const navigateWeek = (direction) => {
    const newWeek = new Date(currentWeek)
    newWeek.setDate(currentWeek.getDate() + (direction * 7))
    setCurrentWeek(newWeek)
  }

  const getPlatformIcon = (platform) => {
      if (platform === 'facebook') {
        return (
          <img
          src="/assets/icons/facebook-icon.png"
          alt="Facebook Icon"
          width="13"
          height="13"
          />
        )
      } else if (platform === 'instagram') {
        return (
          <img
          src="/assets/icons/insta-icon.png"
          alt="Facebook Icon"
          width="16"
          height="16"
          />
        )
      }
    }


  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return '#1E478A'
      case 'scheduled': return '#F59E0B'
      case 'draft': return '#6B7280'
      default: return '#6B7280'
    }
  }

  return (
    <div className={`social-media-ai-page ${connectModalOpen ? 'blurred' : ''}`}>
      {/* Connect Page Modal */}
      <ConnectPageModal
        isOpen={connectModalOpen}
        onClose={() => {
          // Only allow closing if Facebook with at least one page OR Instagram is connected
          const facebookConnected = rootElement?.getAttribute('facebook-connected') === 'true';
          const instagramConnected = rootElement?.getAttribute('instagram-connected') === 'true';
          let pagesLen = 0;
          try {
            const pagesJson = rootElement?.getAttribute('facebook-pages') || '[]';
            const pages = JSON.parse(pagesJson);
            pagesLen = Array.isArray(pages) ? pages.length : 0;
          } catch (_e) { pagesLen = 0; }
          const fbReady = facebookConnected && pagesLen > 0;
          if (fbReady || instagramConnected) {
            setConnectModalOpen(false);
          }
        }}
        onFacebookConnect={() => setConnectModalOpen(false)}
      />

      {/* Edit Post Modal */}
      <EditPostModal
        isOpen={modalOpen}
        post={currentPost}
        onClose={closeModal}
        onSave={savePostChanges}
        onDelete={handleDelete}
        promotedEvents={promotedEvents}
      />

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={handleCreatePost}
        promotedEvents={promotedEvents}
        alertConfigurations={alertConfigurations}
      />

      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onPostTargetsSaved={refreshCompanyPostsData}
      />

      {/* Progress Header */}
      {isGenerating && (
        <div className="generation-progress-header">
          <div className="generation-progress-content">
            <div className="generation-progress-spinner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" strokeDasharray="31.416" strokeDashoffset="31.416" strokeLinecap="round">
                  <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416;0 31.416" repeatCount="indefinite"/>
                  <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416;-31.416" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            <div className="generation-progress-text">
              <strong>Generating Posts...</strong>
              <span>{progressMessage}</span>
            </div>
            <div className="generation-progress-estimate">
              Estimated time: 1-5 minutes
            </div>
          </div>
        </div>
      )}

      <div className="social-media-content">
        <div className="content-header mb-0">
          <div className="date-navigation">
            <button className="nav-btn" onClick={() => navigateWeek(-1)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <h2 className="week-range">{getWeekRange(currentWeek)}</h2>
            <button className="nav-btn" onClick={() => navigateWeek(1)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* <button className="generate-post-btn" onClick={() => { window.location.href = '/marketing/social_media_ai/campaign' }}>
              Draft with AI
            </button> */}
            <button className="generate-post-btn" onClick={() => setCreateModalOpen(true)}>
              Create Post
            </button>
            <button
              className="settings-btn"
              title="Settings"
              onClick={() => { setConnectModalOpen(false); setSettingsOpen(true); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 682.667 682.667">
                <g transform="matrix(1.33333 0 0 -1.33333 0 682.667)">
                  <path
                    d="M256 334.666c-43.446 0-78.667-35.22-78.667-78.667 0-43.446 35.221-78.666 78.667-78.666 43.446 0 78.667 35.22 78.667 78.666 0 43.447-35.221 78.667-78.667 78.667zm220.802-22.53-21.299-17.534c-24.296-20.001-24.296-57.204 0-77.205l21.299-17.534c7.548-6.214 9.497-16.974 4.609-25.441l-42.057-72.845c-4.889-8.467-15.182-12.159-24.337-8.729l-25.835 9.678c-29.469 11.04-61.688-7.561-66.862-38.602l-4.535-27.213c-1.607-9.643-9.951-16.712-19.727-16.712h-84.116c-9.776 0-18.12 7.069-19.727 16.712l-4.536 27.213c-5.173 31.041-37.392 49.642-66.861 38.602l-25.834-9.678c-9.156-3.43-19.449.262-24.338 8.729l-42.057 72.845c-4.888 8.467-2.939 19.227 4.609 25.441l21.3 17.534c24.295 20.001 24.295 57.204 0 77.205l-21.3 17.534c-7.548 6.214-9.497 16.974-4.609 25.441l42.057 72.845c4.889 8.467 15.182 12.159 24.338 8.729l25.834-9.678c29.469-11.04 61.688 7.561 66.861 38.602l4.536 27.213c1.607 9.643 9.951 16.711 19.727 16.711h84.116c9.776 0 18.12-7.068 19.727-16.711l4.535-27.213c5.174-31.041 37.393-49.642 66.862-38.602l25.835 9.678c9.155 3.43 19.448-.262 24.337-8.729l42.057-72.845c4.888-8.467 2.939-19.227-4.609-25.441z"
                    fill="none"
                    stroke="#000"
                    strokeWidth="40"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </svg>
            </button>
          </div>
        </div>

        <div className="calendar-container">

          <div className="calendar-grid">
            {weekDates.map((date, index) => {
              const dateKey = formatDate(date)
              const dayPosts = postsData[dateKey] || []

              return (
                <div key={index} className="calendar-day">
                  <div className="day-header text-center">
                    <h3>{formatDisplayDate(date)}</h3>
                  </div>
                  <div className="day-posts">
                    {dayPosts.map((post) => (
                      <div key={post.id} className="post-card" onClick={() => openEditModal(post)}>
                        {/* Header with profile picture and title */}
                        <div className="post-header">
                          <div className="post-profile-pic">
                            <img
                              src={post.event_url}
                              alt={post.title}
                              className="profile-image"
                            />
                          </div>
                          <div className="post-title-text">
                            {post.post_type === 'roundup_post' ? 'Roundup Post' : post.event_title}
                          </div>
                        </div>

                        {/* Main promotional image */}
                        <div className="post-main-image">
                          {(post.media_type?.includes("video") || post.image?.match(/\.(mp4|webm|ogg)$/i)) ? (
                            <VideoThumbnail
                              videoUrl={post.image}
                              alt="Video thumbnail"
                              className="main-image"
                            />
                          ) : (
                            <img
                              src={post.image}
                              alt={post.title}
                              className="main-image"
                            />
                          )}
                        </div>

                        {/* Text description */}
                        <div className="post-description">
                          <p>{post.description}</p>
                        </div>

                        {/* Footer with status, time, and platform icons */}
                        <div className={`post-footer ${post.status}`}>
                          <div className="post-status">
                            {post.status === 'scheduled' ? 'APPROVED' : post.status === 'published' ? 'POSTED' : post.status === 'partially_published' ? 'PARTIALLY POSTED' : post.status.toUpperCase()}
                          </div>
                          <div className="post-dots">•</div>
                          <div className="post-time">
                            {post.time}
                          </div>
                          <div className="post-dots">•</div>
                          <div className="post-platforms">
                            {(post.platform_statuses && post.platform_statuses.length > 0
                              ? post.platform_statuses.map((ps, idx) => (
                                <span key={idx} className={`platform-icon ${ps.status === 'failed' ? 'platform-failed' : ''}`}>
                                  {getPlatformIcon(ps.platform)}
                                </span>
                              ))
                              : post.platforms.map((platform, idx) => (
                                <span key={idx} className="platform-icon">
                                  {getPlatformIcon(platform)}
                                </span>
                              ))
                            )}
                          </div>
                        </div>

                        {/* Edit overlay */}
                        <div className="edit-overlay">
                          <button
                            className="edit-button"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent card click when button is clicked
                              openEditModal(post);
                            }}
                          >
                            View/Edit
                          </button>
                        </div>

                        {/* Action buttons overlay */}
                        {/* <div className="post-actions">
                          <button
                            className="action-btn"
                            style={{ color: getStatusColor(post.status) }}
                            title={`Status: ${post.status}`}
                          >
                             <img src="/assets/icons/tick.svg" alt="Facebook Icon"  width="12"  height="12" />
                          </button>
                          <button
                            className="action-btn"
                            onClick={() => openEditModal(post)}
                            title="Edit post"
                          >
                             <img src="/assets/icons/edit.svg" alt="Facebook Icon"  width="12"  height="12" />
                          </button>
                          <button
                            className="action-btn"
                            title="Delete post"
                            onClick={() => handleDelete(post.id)}
                          >
                            <img
                              src="/assets/icons/delete.svg"
                              alt="Delete Icon"
                              width="12"
                              height="12"
                            />
                          </button>
                        </div> */}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SocialMediaAIPage
